import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Select, InputNumber, Input, Tooltip, Popconfirm, Row, Col, Tag, Radio, Segmented, Table as AntTable, Modal, Alert } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import Icons from '@/components/third-party/Icons';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import TableContainer from '@/components/TableContainer';
import CascadingRecordPicker from '@/components/CascadingRecordPicker';
import useAppSetting from '@/hooks/useAppSetting';
import { get_bill_of_materials } from '@/utils/api';
import {
  get_recipe_bom_item_list,
  get_recipe_mbom,
  add_recipe_bom_item,
  update_recipe_bom_item,
  delete_recipe_bom_item,
  update_recipe_bom_notes,
  get_recipe_variant_list,
  get_formula_definitions,
  get_formula_usages,
  get_formula_domain_code_mappings,
  get_formula_mappable_models,
  get_formula_mappable_model_rows,
  get_formula_model_field_values,
  get_formula_model_filter_path,
  test_formula_usage,
  update_formula_usage,
  get_recipe_meta_form,
  bp_units_data,
  bp_inventory_data,
  bp_get_parameters,
} from '@/utils/api';

const RULE_TYPE_OPTIONS = [
  { value: 'PER_UNIT', label: 'Per Unit' },
  { value: 'PER_VOLUME', label: 'Per Volume' },
  { value: 'PER_MASS', label: 'Per Mass' },
  { value: 'PERCENT_WW', label: '% w/w' },
  { value: 'PERCENT_WV', label: '% w/v' },
  { value: 'PERCENT_VV', label: '% v/v' },
  { value: 'FIXED_BATCH_QTY', label: 'Fixed' },
  { value: 'QS_FINAL_VOLUME', label: 'QS to Final Volume' },
  { value: 'QS_FINAL_WEIGHT', label: 'QS to Final Weight' },
  { value: 'QS_TARGET_PARAMETER', label: 'QS to Target Parameter' },
  { value: 'QS_PROCESS', label: 'QS as Required' },
  { value: 'FIXED_COUNT', label: 'Count' },
];
// Mirrors MfrLine.ROLE_CHOICES / MasterFormulaRecords.js's ROLE_COLOR (2026-09-01, "show the
// material category as role — API, excipient and so on") — the classification the old per-line
// Formula table had, now on Recipe_BOM_Item itself.
const ROLE_OPTIONS = [
  { value: 'API', label: 'Active ingredient' }, { value: 'EXCIPIENT', label: 'Excipient' },
  { value: 'PH_ADJUST', label: 'pH adjuster' }, { value: 'SOLVENT', label: 'Solvent / vehicle' },
  { value: 'GAS', label: 'Process gas' }, { value: 'PROCESS_AID', label: 'Processing aid (single use)' },
  { value: 'PRIMARY_PM', label: 'Primary packaging' }, { value: 'SECONDARY_PM', label: 'Secondary packaging' },
  { value: 'PRINTED_PM', label: 'Printed packaging (artwork-controlled)' },
];
const ROLE_COLOR = { API: 'geekblue', EXCIPIENT: 'default', PH_ADJUST: 'purple', SOLVENT: 'cyan', GAS: 'lime', PROCESS_AID: 'orange', PRIMARY_PM: 'gold', SECONDARY_PM: 'gold', PRINTED_PM: 'magenta' };
// The roles issued against packaging materials (bp_material.is_packaging) rather than
// manufacturing ones — the only split bp_material_inventory actually carries.
const PACKAGING_ROLES = ['PRIMARY_PM', 'SECONDARY_PM', 'PRINTED_PM'];
const PER_OUTPUT_UNIT_RULE_TYPES = ['PER_UNIT', 'FIXED_COUNT'];
// QS_PROCESS ("as required") has no rule_value driving any calculation — but
// the Value field still shows for it, purely as an optional reference/typical
// quantity for documentation (e.g. "usually ~50kg"). Never required.
const HIDE_VALUE_RULE_TYPES = [];
const PERCENT_RULE_TYPES = ['PERCENT_WW', 'PERCENT_WV', 'PERCENT_VV'];
// These two dose at a rate "per 1 [rule_uom]" — rule_uom is a real, pickable
// unit (mL vs L, mg vs g vs kg), filtered by category, since different
// clients dose at very different scales. Not "per 1 recipe unit" anymore.
const UOM_BASIS_RULE_TYPES = ['PER_VOLUME', 'PER_MASS'];
const RULE_TYPE_UNIT_CATEGORY = { PER_VOLUME: 'VOLUME', PER_MASS: 'WEIGHT' };
// For these, Value <-> Std Qty is a real, non-trivial ratio — worth letting
// the user enter either one and have the other back-solved (e.g. "I know
// this should come out to 819.882kg, what % w/w is that?"). Everything else
// (Fixed, QS_*) is already a direct/near-direct pass-through, so there's
// nothing meaningful to reverse.
const REVERSIBLE_RULE_TYPES = [...PERCENT_RULE_TYPES, ...UOM_BASIS_RULE_TYPES, ...PER_OUTPUT_UNIT_RULE_TYPES];
// None of these are actually computable at recipe-design time — real QS
// resolution only happens live at batch execution. Both fields (Rule/target
// and Std Qty) are just independent, optional reference values here, saved
// as given, with no formula between them.
const QS_TYPES = ['QS_FINAL_VOLUME', 'QS_FINAL_WEIGHT', 'QS_TARGET_PARAMETER', 'QS_PROCESS'];

// Caps display and trims trailing zeros (13.858007658496307 -> "13.86" at 2
// decimals, 820.000 -> "820") — a reverse-solved rule_value (target qty /
// batch size) rarely divides evenly, but nobody reads a %w/w to more than 2
// decimals, or most other values past 3, in practice.
// FRM-01 A4-2: a BOM line whose attached correction is pending (formula not active / not
// evaluable — the backend stores corrected_quantity null) has NO effective quantity. Never fall
// back to Std Qty for it: that would read as a finished, corrected number.
const CORRECTION_PENDING_TEXT = 'pending — correction formula not active';
const isCorrectionPending = (r) =>
  !!r && (r.correction_pending === true || (!!r.correction_formula_id && r.corrected_quantity == null));
const effectiveQty = (r) => (isCorrectionPending(r) ? null : r?.corrected_quantity ?? r?.computed_quantity);

const formatRuleValue = (v, decimals = 3) => {
  if (v === null || v === undefined) return '-';
  return Number(v).toFixed(decimals).replace(/\.?0+$/, '');
};

const formatFormulaTestError = (data) => {
  if (!data) return null;
  if (typeof data === 'string') return data;
  if (data.detail) return data.detail;
  const parts = Object.entries(data).map(([field, msgs]) => {
    const text = Array.isArray(msgs) ? msgs.join(' ') : String(msgs);
    return field === 'non_field_errors' ? text : `${field}: ${text}`;
  });
  return parts.length ? parts.join(' | ') : null;
};

const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);
// 2026-09-01: "add labels then" — a discriminator column is a raw Django filter-kwarg path
// ("batch__material_batch_number"), not something to show as-is; "__" is relation traversal
// (rendered as "›"), "_" is a word boundary within one hop.
const humanizeColumn = (column) => (column || '').split('__').map((hop) => capitalize(hop.split('_').join(' '))).join(' › ');

// A raw id is meaningless to a tester on its own — build a human label from
// a record's own display columns instead (lot number, batch number,
// material name, ...), same convention CascadingRecordPicker's rowLabel
// uses, so a resolved record reads as e.g. "LOT-2024-0016 · ATRCA" rather
// than just "16".
const rowDisplayLabel = (row, columns) =>
  (columns || [])
    .map((c) => (typeof row[c] === 'string' ? row[c] : ''))
    .filter(Boolean)
    .slice(0, 3)
    .join(' · ');

const emptyForm = {
  bom_item_id: null,
  material_inventory_id: null,
  role: null,
  alias: '',
  group_label: '',
  // % w/w + entering the target Std Qty directly are overwhelmingly the most
  // common way this BOM is authored — default straight into that instead of
  // making every line pick the rule type and flip the toggle first.
  rule_type: 'PERCENT_WW',
  entry_mode: 'target',
  rule_value: '',
  target_quantity: '',
  qs_std_quantity: '',
  reference_batch_quantity: '',
  rule_uom_id: null,
  target_parameter_id: null,
  recipe_variant_id: null,
  correction_formula_id: null,
  // Reference-only (2026-09-01, requested to match the old per-line Formula table's Overage/
  // Tolerance columns): not read by bom_calculation.py — Std/Corrected Qty are unaffected by
  // these. Overage % is normally already expressible via a correction_formula picked above (it's
  // one of the things that layer can encode); these are a simpler, always-available pair for
  // when a formula isn't needed, same as this BOM's own rule_value/qs_std_quantity fields.
  overage_percent: '',
  tolerance_percent: '',
};

// Recipe-level BOM — "this material is dosed like this" — authored once per
// recipe, before any process/operation exists. Operations pick one of these
// lines (in Ingredients config) to auto-fill their own material + rule
// fields; editing a line here afterward never touches an already-configured
// operation (copy-in, not a live link).
/** The record's bill of materials as the batch uses it: the MFR scaled to the batch size, plus
 *  the record's own lines (packaging, filters, gas). Read-only — it changes with the MFR or the
 *  batch size (re-derive), never by typing here. Shown only when the record has one. */
function DerivedBom({ recipeId, textColor, onLoaded, batchSize, batchUom, massUom }) {
  const [mbom, setMbom] = useState(null);
  const [loading, setLoading] = useState(false);
  // 2026-10-15 (owner) · the BOM at the batch size typed in the form — recalculated from the MFR
  // as it changes (the same server calculation View BOM and the batch use), not the saved figures.
  const [calc, setCalc] = useState(null);
  useEffect(() => {
    const size = Number(batchSize);
    if (!recipeId || !Number.isFinite(size) || size <= 0) { setCalc(null); return undefined; }
    const t = setTimeout(() => {
      get_bill_of_materials({ recipe_id: recipeId, batch_size: size, ...(batchUom ? { batch_uom: batchUom } : {}), ...(massUom ? { mass_uom: massUom } : {}) }, (res) => {
        const body = res?.data || {};
        setCalc(Array.isArray(body.data) ? { lines: body.data, size, uom: batchUom || body.batch_uom || '', source: body.source, mfr: body.mfr } : null);
      });
    }, 350);
    return () => clearTimeout(t);
  }, [recipeId, batchSize, batchUom, massUom]);
  useEffect(() => {
    if (!recipeId) return;
    setLoading(true);
    get_recipe_mbom((res) => {
      setLoading(false);
      // get_function hands over the axios response: the body is res.data, the bill is its `data`.
      const body = res?.data ?? res;
      const d = Array.isArray(body?.lines) ? body : body?.data;
      const has = !!(d && Array.isArray(d.lines) && d.lines.length);
      setMbom(has ? d : null);
      onLoaded?.(has);
    }, recipeId);
  }, [recipeId]);
  if (loading && !mbom) {
    return (
      <div className="mb-4">
        <span className="text-sm font-semibold" style={{ color: textColor }}>Bill of materials</span>
        <AntTable size="small" loading dataSource={[]} pagination={false} style={{ marginTop: 8 }}
                  columns={[{ title: 'Material' }, { title: 'SAP code' }, { title: 'Role' }, { title: 'Quantity for the batch' }]}
                  locale={{ emptyText: 'Loading the bill of materials…' }} />
      </div>
    );
  }
  if (!mbom) return null;
  const n = (v) => (v === null || v === undefined || v === '' ? '' : String(+Number(v).toFixed(6)));
  const key = (v) => String(v || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const calcBy = {};
  (calc?.lines || []).forEach((c) => { calcBy[`sap:${c.sap_code}`] = c; calcBy[`name:${key(c.material_name)}`] = c; });
  const live = (l) => (calc ? (l.sap_code && calcBy[`sap:${l.sap_code}`]) || calcBy[`name:${key(l.material)}`] || null : null);
  const rows = mbom.lines.map((l) => (live(l) && !l.is_qs ? {
    key: l.sequence, seq: l.sequence, material: l.material, sap: l.sap_code, role: l.role,
    perBasis: live(l).source === 'MFR' && calc.size ? `${n(live(l).batch_quantity / calc.size)} ${live(l).unit || ''} per ${calc.uom || 'unit'}`.trim() : '—',
    qty: `${n(live(l).batch_quantity)} ${live(l).unit || ''}`.trim(),
    source: live(l).source === 'MFR' ? 'MFR' : 'record',
    calc: live(l).source === 'MFR'
      ? `MFR at ${n(calc.size)} ${calc.uom || ''} = ${n(live(l).batch_quantity)} ${live(l).unit || ''}`.replace(/\s+/g, ' ').trim()
      : `record line × ${n(calc.size)} ÷ ${n(mbom.batch_size)} ${mbom.batch_uom || ''}`.replace(/\s+/g, ' ').trim(),
  } : {
    key: l.sequence,
    seq: l.sequence,
    material: l.material,
    sap: l.sap_code,
    role: l.role,
    perBasis: l.per_basis != null ? `${n(l.per_basis)} ${l.per_basis_uom || ''}`.trim() : '—',
    qty: l.is_qs ? `q.s.${l.qs_target ? ` — ${l.qs_target}` : ''}` : `${n(l.dispense_quantity ?? l.quantity)} ${l.dispense_uom || l.uom || ''}`.trim(),
    source: l.mfr_line_id ? 'MFR' : 'record',
    // the sum itself: MFR quantity per basis × how many bases the batch is = quantity for the batch
    calc: l.mfr_line_id && l.per_basis != null && !l.is_qs && mbom.scale_factor
      ? `${n(l.per_basis)} ${l.per_basis_uom || ''} per ${mbom.basis ? `${n(mbom.basis.quantity)} ${mbom.basis.uom}` : 'basis'} × ${Number(mbom.scale_factor).toLocaleString()}${mbom.batch_size != null ? ` (${n(mbom.batch_size)} ${mbom.batch_uom || ''})` : ''} = ${n(l.dispense_quantity ?? l.quantity)} ${l.dispense_uom || l.uom || ''}`.replace(/\s+/g, ' ').trim()
      : l.is_qs ? 'quantity sufficient — made up at execution' : l.mfr_line_id ? '' : 'set on the record',
  }));
  const shownSize = calc ? calc.size : mbom.batch_size;
  const shownUom = calc ? calc.uom : mbom.batch_uom;
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2" style={{ gap: 8, flexWrap: 'wrap' }}>
        <span className="text-sm font-semibold" style={{ color: textColor }}>
          Bill of materials{shownSize != null ? ` — for ${n(shownSize)} ${shownUom || ''} batch` : ''}
        </span>
        <span style={{ fontSize: 12.5, color: 'var(--dz-faint, #6b7280)' }}>
          {calc?.mfr ? `Calculated from ${calc.mfr} at this batch size · ` : mbom.mfr ? `Derived from ${mbom.mfr.mfr_no} v${mbom.mfr.version} · ` : ''}{mbom.lines.length} lines · read-only
        </span>
      </div>
      <AntTable
        size="small" dataSource={rows} scroll={{ x: true }}
        pagination={{ pageSize: 5, showSizeChanger: false, size: 'small', hideOnSinglePage: true,
                      showTotal: (t, [a, b]) => `${a}–${b} of ${t} lines` }}
        columns={[
          { title: '#', dataIndex: 'seq', width: 48 },
          { title: 'Material', dataIndex: 'material' },
          { title: 'SAP code', dataIndex: 'sap', width: 110 },
          { title: 'Role', dataIndex: 'role', width: 120 },
          { title: 'Per basis (MFR)', dataIndex: 'perBasis', width: 130 },
          { title: 'Quantity for the batch', dataIndex: 'qty', width: 170 },
          { title: 'Calculation (from batch size)', dataIndex: 'calc', width: 320 },
          { title: 'From', dataIndex: 'source', width: 80,
            render: (v) => <Tag color={v === 'MFR' ? 'blue' : 'default'}>{v}</Tag> },
        ]}
      />
      <div style={{ fontSize: 12.5, color: 'var(--dz-faint, #6b7280)', marginTop: 6 }}>
        MFR lines are the MFR quantity × the batch size, recalculated as the batch size above changes; "record" lines are this record's own (packaging, filters, gas), scaled to the batch size. This is the BOM View BOM shows and every batch is dispensed against.
      </div>
    </div>
  );
}

export default function RecipeBomPanel({ recipeId, batchSize, batchUom, massUom }) {
  const { isDark } = useTheme();
  // These three rule types vary a lot by client (different units/parameters
  // per plant) — switchable off in Application Settings. Default on.
  const perVolumeEnabled = useAppSetting('Enable Per Volume Rule', true);
  const perMassEnabled = useAppSetting('Enable Per Mass Rule', true);
  const qsTargetParameterEnabled = useAppSetting('Enable QS Target Parameter Rule', true);
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const formBgColor = isDark ? defaultColors.palette.gray[700] : '#f8faff';
  const accentColor = '#3b82f6';
  const accentBg = isDark ? 'rgba(59,130,246,0.08)' : '#eff6ff';
  const [items, setItems] = useState([]);
  // 2026-10-12 · whether the record has a bill derived from its MFR (DerivedBom). Then the
  // hand-entered BOM below is not what the batch uses, and while it is empty it is not shown —
  // two BOM tables, one of them empty, read as the bill being missing.
  const [hasDerived, setHasDerived] = useState(false);
  const [materials, setMaterials] = useState([]);          // manufacturing materials
  const [packMaterials, setPackMaterials] = useState([]);  // packaging materials (module_type=pack)
  const [units, setUnits] = useState([]);
  const [variants, setVariants] = useState([]);
  const [formulas, setFormulas] = useState([]);
  const [formulaUsages, setFormulaUsages] = useState([]);
  const [domainCodes, setDomainCodes] = useState([]);
  const [modelVerboseNames, setModelVerboseNames] = useState({});
  const [parameters, setParameters] = useState([]);
  const [recipeUnitId, setRecipeUnitId] = useState(null);
  const [recipeMasterQty, setRecipeMasterQty] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [bomNotes, setBomNotes] = useState('');
  const [savedBomNotes, setSavedBomNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [selectedUsageId, setSelectedUsageId] = useState(null);
  const [testOverrides, setTestOverrides] = useState({});
  // 2026-09-01: per-binding test-time filter values for a "Reference Value by filter" mapping
  // (value_discriminator_columns set, e.g. Assay/water_content off a Material Inventory Lot,
  // keyed by batch/lot) — { [variable_name]: { [column]: value } }, sent as source_config_overrides
  // and merged into that binding's own source_config just for this test run. "Same record for
  // all"'s record-id picker structurally cannot satisfy this kind of binding (the resolver never
  // reads value_record_ids for it — see registry.py._resolve_reference_value_by_filter), so it
  // needs its own input, not a repurposed one.
  const [testSourceConfigOverrides, setTestSourceConfigOverrides] = useState({});
  const [testDependentValues, setTestDependentValues] = useState({});
  const [testValueRecordIds, setTestValueRecordIds] = useState({});
  // How many rows the BOM-context filters (pickerFiltersForModel) narrowed
  // a needed model down to, keyed by model — 0/unset (nothing narrowed it,
  // use Lookup to search normally) and 1 (auto-filled into
  // testValueRecordIds directly) both need no further prompt; >1 means real
  // ambiguity the tester has to resolve, surfaced via the Lookup popup
  // (opened automatically the first time — see the auto-resolve effect and
  // autoOpenedForModel below) so they pick from real rows with real column
  // headers rather than guessing an id.
  const [testValueCandidates, setTestValueCandidates] = useState({});
  // Which models this dialog has already auto-opened the Lookup popup for
  // — so ambiguity found on a later re-check (e.g. after the tester closes
  // the popup without picking) doesn't keep forcing it back open.
  const autoOpenedForModel = useRef(new Set());
  // A raw database id means nothing to a tester on its own — this pairs
  // each testValueRecordIds/testBatchId entry with a human label (lot
  // number, batch number, material name, ...) built from the record's own
  // display columns, so the UI can show what was actually picked instead of
  // just a number. Cleared whenever the id is changed by hand (typed or
  // edited directly), since at that point the label is no longer known.
  const [testValueRecordLabels, setTestValueRecordLabels] = useState({});
  // Per-binding record id override, used instead of testValueRecordIds when
  // a model's toggle (testRecordMode) is set to "different" — two bindings
  // can share a value-model but need two different rows (e.g. a "dependent
  // material" reading and "this material"'s own reading, both off
  // djangoapp.recipe_bom_item, at different ids). Keyed by variable_name,
  // sent as value_record_ids_by_binding — see registry.py resolve().
  const [testValueRecordIdsByBinding, setTestValueRecordIdsByBinding] = useState({});
  // 'same' (one shared record for every binding needing this model — the
  // original, only option) or 'different' (one record per binding), keyed
  // by modelLabel. No stored default: computed lazily at render time from
  // testValueModelBindings (>1 binding sharing a model defaults to
  // "different", the safe choice — see testValueModelBindings below).
  const [testRecordMode, setTestRecordMode] = useState({});
  const [testBomSources, setTestBomSources] = useState({});
  const [testBatchId, setTestBatchId] = useState('');
  const [testBatchLabel, setTestBatchLabel] = useState('');
  const [testingFormula, setTestingFormula] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [lookupState, setLookupState] = useState({
    open: false,
    modelLabel: '',
    title: '',
    target: null,
  });

  const fetchItems = () => {
    if (!recipeId) return;
    get_recipe_bom_item_list((res) => {
      if (res?.data?.status === 'success') {
        setItems(res?.data?.data || []);
        setBomNotes(res?.data?.bom_notes || '');
        setSavedBomNotes(res?.data?.bom_notes || '');
      }
    }, { recipe_id: recipeId });
  };

  const fetchFormulaUsages = () => {
    get_formula_usages((data) => {
      setFormulaUsages((Array.isArray(data) ? data : []).filter((u) => u.is_active !== false));
    });
  };

  const handleSaveNotes = () => {
    setSavingNotes(true);
    update_recipe_bom_notes((res) => {
      setSavingNotes(false);
      if (res?.status === 'success' || res?.data?.status === 'success') {
        toast.success('BOM notes saved');
        setSavedBomNotes(bomNotes);
      } else {
        toast.warning(res?.message || res?.data?.message || 'Failed to save notes');
      }
    }, { recipe_id: recipeId, bom_notes: bomNotes });
  };

  useEffect(() => {
    fetchItems();
    // 2026-09-21: this used to filter on material_type === 'Ingredients', which matched nothing —
    // every bp_material_inventory row carries material_type 'Raw Material', so the dropdown came
    // back empty and no Recipe_BOM_Item could be added through the UI at all (0 rows estate-wide).
    //
    // The split the inventory does carry is bp_material.is_packaging, and Get_Material_inventory
    // serves one side or the other: no module_type gives the manufacturing materials, module_type
    // 'pack' gives the packaging ones. Both are fetched so the Role picked on the form decides
    // which list is offered (materialOptions).
    bp_inventory_data({}, (res) => {
      if (res?.data?.status === 'success') setMaterials(res?.data?.data || []);
    });
    bp_inventory_data({ module_type: 'pack' }, (res) => {
      if (res?.data?.status === 'success') setPackMaterials(res?.data?.data || []);
    });
    bp_units_data((res) => {
      if (res?.data?.status === 'success') setUnits(res?.data?.data || []);
    });
    get_formula_definitions((data) => {
      setFormulas((Array.isArray(data) ? data : []).filter((f) => f.status === 'active'));
    });
    fetchFormulaUsages();
    get_formula_domain_code_mappings((res) => {
      const list = res?.data?.data;
      if (Array.isArray(list)) setDomainCodes(list.filter((m) => m.is_active));
    });
    get_formula_mappable_models((res) => {
      const list = res?.data?.data;
      if (Array.isArray(list)) {
        const map = {};
        list.forEach((m) => {
          map[m.label] = m.verbose_name;
        });
        setModelVerboseNames(map);
      }
    });
    bp_get_parameters((res) => {
      if (res?.data?.status === 'success') setParameters(res?.data?.data || []);
    });
    if (recipeId) {
      get_recipe_variant_list((res) => {
        if (res?.data?.status === 'success') setVariants(res?.data?.data || []);
      }, { recipe_id: recipeId });
      get_recipe_meta_form((res) => {
        setRecipeUnitId(res?.data?.data?.unit || null);
        setRecipeMasterQty(res?.data?.data?.master_batch_size ?? null);
      }, { recipe_id: recipeId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeId]);

  const resetForm = () => {
    setShowForm(false);
    setForm(emptyForm);
    setSelectedUsageId(null);
  };

  const resetTestState = () => {
    setSelectedUsageId(null);
    setTestOverrides({});
    setTestSourceConfigOverrides({});
    setTestDependentValues({});
    setTestValueRecordIds({});
    setTestValueCandidates({});
    setTestValueRecordLabels({});
    setTestValueRecordIdsByBinding({});
    setTestRecordMode({});
    setTestBomSources({});
    setTestBatchId('');
    setTestBatchLabel('');
    setTestingFormula(false);
    setTestResult(null);
    setLookupState({ open: false, modelLabel: '', title: '', target: null });
    autoOpenedForModel.current = new Set();
  };

  const handleEdit = (record) => {
    setForm({
      bom_item_id: record.bom_item_id,
      material_inventory_id: record.material_inventory_id,
      role: record.role || null,
      alias: record.alias || '',
      group_label: record.group_label || '',
      rule_type: record.rule_type,
      entry_mode: 'value',
      rule_value: record.rule_value ?? '',
      target_quantity: '',
      qs_std_quantity: record.qs_std_quantity ?? '',
      reference_batch_quantity: record.reference_batch_quantity ?? '',
      rule_uom_id: record.rule_uom_id || null,
      target_parameter_id: record.target_parameter_id || null,
      recipe_variant_id: record.recipe_variant_id,
      correction_formula_id: record.correction_formula_id,
      overage_percent: record.overage_percent ?? '',
      tolerance_percent: record.tolerance_percent ?? '',
    });
    setSelectedUsageId(record.correction_formula_usage_id || null);
    setShowForm(true);
  };

  const handleSave = () => {
    if (!form.material_inventory_id) {
      toast.warning('Material is required');
      return;
    }
    setSaving(true);
    const payload = {
      material_inventory_id: form.material_inventory_id,
      role: form.role || null,
      alias: form.alias?.trim() || null,
      group_label: form.group_label?.trim() || null,
      rule_type: form.rule_type || null,
      // Only the active mode's field is sent with a real value — the other
      // stays null, so the server knows which direction to solve (a real
      // rule_value always wins if somehow both were set).
      rule_value: form.entry_mode === 'value' && form.rule_value !== '' ? Number(form.rule_value) : null,
      target_quantity: form.entry_mode === 'target' && form.target_quantity !== '' ? Number(form.target_quantity) : null,
      qs_std_quantity: form.qs_std_quantity === '' ? null : Number(form.qs_std_quantity),
      reference_batch_quantity: form.reference_batch_quantity === '' ? null : Number(form.reference_batch_quantity),
      rule_uom_id: form.rule_uom_id || null,
      target_parameter_id: form.target_parameter_id || null,
      recipe_variant_id: form.recipe_variant_id || null,
      correction_formula_id: form.correction_formula_id || null,
      overage_percent: form.overage_percent === '' ? null : Number(form.overage_percent),
      tolerance_percent: form.tolerance_percent === '' ? null : Number(form.tolerance_percent),
    };
    const onOk = (res) => {
      setSaving(false);
      if (res?.status === 'success' || res?.data?.status === 'success') {
        toast.success(res?.message || res?.data?.message || 'BOM item saved');
        fetchItems();
        resetForm();
      } else {
        toast.warning(res?.message || res?.data?.message || 'Failed to save BOM item');
      }
    };
    if (form.bom_item_id) {
      update_recipe_bom_item(onOk, { ...payload, bom_item_id: form.bom_item_id });
    } else {
      add_recipe_bom_item(onOk, { ...payload, recipe_id: recipeId });
    }
  };

  const handleDelete = (record) => {
    delete_recipe_bom_item((res) => {
      if (res?.status === 'success' || res?.data?.status === 'success') {
        toast.success('BOM item deleted');
        fetchItems();
      } else {
        toast.warning(res?.message || res?.data?.message || 'Failed to delete BOM item');
      }
    }, { bom_item_id: record.bom_item_id });
  };

  // 2026-09-21, "role pehle aayega na uske baad material aayenge" — the role is picked first and
  // it narrows the materials. The three packaging roles are issued against packaging materials,
  // every other role against manufacturing ones. With no role chosen yet the form offers both, so
  // an existing line (saved before roles were filtered, or with no role at all) still resolves its
  // own material name in the table and the summary below.
  const materialOptions = useMemo(() => {
    const all = [...materials, ...packMaterials];
    const src = form.role
      ? (PACKAGING_ROLES.includes(form.role) ? packMaterials : materials)
      : all;
    const opts = src.map((m) => ({ value: m.material_id, label: m.material_name }));
    // A row saved before this filter existed can hold a material its role would not offer (a
    // packaging component filed under EXCIPIENT, say). Keep whatever is actually selected in the
    // list so editing that row shows its material rather than a blank, and re-saving cannot
    // silently drop it. Nothing here narrows what a NEW line may pick.
    const sel = form.material_inventory_id;
    if (sel && !opts.some((o) => String(o.value) === String(sel))) {
      const m = all.find((x) => String(x.material_id) === String(sel));
      if (m) opts.unshift({ value: m.material_id, label: m.material_name });
    }
    return opts;
  }, [materials, packMaterials, form.role, form.material_inventory_id]);
  // Groups already used elsewhere in this BOM — offered as pickable options,
  // but still a free-text field (mode="tags"): pick an existing one to stay
  // consistent, or just type a new one.
  const existingGroupOptions = [...new Set(items.map((i) => i.group_label).filter(Boolean))].map((g) => ({ value: g, label: g }));
  const variantOptions = variants.map((v) => ({
    value: v.variant_id,
    label: v.name || `${v.value} ${v.uom_name || ''}`,
  }));
  const formulaOptions = formulas.map((f) => ({ value: f.id, label: f.name }));
  const selectedFormulaExpression = formulas.find((f) => String(f.id) === String(form.correction_formula_id))?.expression || '';
  const parameterOptions = parameters.map((p) => ({ value: p.id, label: p.parameter_name }));
  const matchingFormulaUsages = useMemo(
    () =>
      formulaUsages.filter(
        (u) => form.correction_formula_id && String(u.formula) === String(form.correction_formula_id)
      ),
    [formulaUsages, form.correction_formula_id]
  );
  const selectedFormulaUsage = matchingFormulaUsages.find((u) => String(u.id) === String(selectedUsageId)) || matchingFormulaUsages[0] || null;
  const usageOptions = matchingFormulaUsages.map((u) => ({
    value: u.id,
    label: u.name || `Usage ${u.id}`,
  }));
  const testNeedsBatchId = useMemo(() => {
    if (!selectedFormulaUsage) return false;
    return (selectedFormulaUsage.input_bindings || []).some((b) =>
      Object.values(b.source_config || {}).some((v) => typeof v === 'string' && v.startsWith('$batch'))
    );
  }, [selectedFormulaUsage]);
  // Filter kwargs that narrow each needed value-model down to rows related
  // to the material inventory chosen for this BOM row — e.g.
  // {"batch__material_inventory_id": 658} for Material Inventory Lot.
  // Populated by the effect below (after testNeededValueModels is defined),
  // which asks the backend to derive the actual FK path for each needed
  // model generically (get_formula_model_filter_path walks the real schema
  // — no per-table map maintained here).
  const [pickerFiltersForModel, setPickerFiltersForModel] = useState({});
  const currentBomRow = useMemo(() => {
    if (!form.bom_item_id) return null;
    return items.find((i) => String(i.bom_item_id) === String(form.bom_item_id)) || null;
  }, [items, form.bom_item_id]);
  // The three pieces of context this specific screen genuinely knows
  // first-hand — not derived from any FK walk, just what's already selected
  // on this form (which BOM row, which recipe, which material inventory).
  // Real Django labels only (djangoapp.recipe_bom_item, not a guessed
  // alternate spelling) — apps.get_models() label_lower is deterministic:
  // app_label + "." + the model class's own name, lowercased, underscores
  // kept exactly as the class defines them.
  const autoContextRecordIds = useMemo(() => {
    const map = {};
    const add = (label, id) => {
      if (id) map[label] = Number(id);
    };
    add('djangoapp.recipe_bom_item', currentBomRow?.bom_item_id || currentBomRow?.id);
    add('djangoapp.bp_recipe_meta_data', recipeId);
    add('djangoapp.bp_material_inventory', form.material_inventory_id);
    return map;
  }, [currentBomRow, recipeId, form.material_inventory_id]);
  const testNeededValueModels = useMemo(() => {
    if (!selectedFormulaUsage) return [];
    const labels = (selectedFormulaUsage.input_bindings || [])
      .map((b) => {
        const mapping = domainCodes.find((m) => m.code === b.source_record_code);
        if (!mapping) return null;
        const needsDirectRecord =
          b.source_type === 'mapped_value' &&
          (!b.source_config || !Object.keys(b.source_config).length) &&
          mapping.model_label;
        return needsDirectRecord
          ? { ...mapping, value_model_label: mapping.model_label }
          : mapping;
      })
      .filter(Boolean)
      .map((m) => m.value_model_label || (m.needs_context_anchor ? m.model_label : null))
      .filter((label) => !autoContextRecordIds[label])
      .filter(Boolean);
    return [...new Set(labels)];
  }, [selectedFormulaUsage, domainCodes, autoContextRecordIds]);
  // Same filtering as testNeededValueModels above, kept in sync by hand,
  // but grouped by binding instead of deduped to a flat label list — needed
  // to know, per model, whether more than one binding is sharing it (the
  // case a "same row for all" shared box would silently force together).
  const testValueModelBindings = useMemo(() => {
    if (!selectedFormulaUsage) return {};
    const map = {};
    (selectedFormulaUsage.input_bindings || []).forEach((b) => {
      const mapping = domainCodes.find((m) => m.code === b.source_record_code);
      if (!mapping) return;
      const needsDirectRecord =
        b.source_type === 'mapped_value' &&
        (!b.source_config || !Object.keys(b.source_config).length) &&
        mapping.model_label;
      const effectiveMapping = needsDirectRecord
        ? { ...mapping, value_model_label: mapping.model_label }
        : mapping;
      const label = effectiveMapping.value_model_label || (effectiveMapping.needs_context_anchor ? effectiveMapping.model_label : null);
      if (!label || autoContextRecordIds[label]) return;
      if (!map[label]) map[label] = [];
      map[label].push(b);
    });
    return map;
  }, [selectedFormulaUsage, domainCodes, autoContextRecordIds]);
  // Which fields on each needed value-model actually matter here — the
  // real value_column (and value_ref_column, for a real-join Reference
  // Value mapping) of every mapping that reads off it, e.g. Assay/water
  // content both read MaterialInventoryLot.normalized_quality_parameter_values
  // — so that field shows in the picker even past the generic column cap,
  // instead of the tester having to guess which lot has the right reading.
  // Driven entirely by the domain mapping data already fetched, not a
  // per-model list.
  const extraColumnsForModel = useMemo(() => {
    const map = {};
    domainCodes.forEach((m) => {
      if (!m.value_model_label) return;
      const fields = [m.value_column, m.value_ref_column].filter(Boolean);
      if (!fields.length) return;
      map[m.value_model_label] = [...new Set([...(map[m.value_model_label] || []), ...fields])];
    });
    return map;
  }, [domainCodes]);
  // The one fact that actually decides whether a binding is fixable here:
  // does it need a row from the SAME table this screen already has loaded
  // (items — this recipe's own BOM rows), or from some other table
  // entirely? Not "is it shaped like a Reference Value mapping" — that was
  // the wrong axis (it made Assay/water_content and a hypothetical
  // different-table Field-Path binding look like separate cases, when the
  // real reason neither is fixable here is identical: this screen simply
  // doesn't have that table's rows loaded). One named constant instead of
  // the literal repeated in every check below — this screen edits
  // Recipe_BOM_Item rows, full stop, so this is what "the same table"
  // means for it.
  const THIS_SCREEN_MODEL_LABEL = 'djangoapp.recipe_bom_item';
  // Bindings that read a specific Recipe_BOM_Item row's own field (this
  // ingredient's — or a different ingredient's — rule/std/corrected
  // quantity). Recipe_BOM_Item rows are recipe-scoped, not batch-scoped,
  // so which row to read is a structural decision, fixed once right here
  // (where the formula is attached to this BOM item) using the rows we
  // already have on screen — or left unset entirely, for whoever runs
  // this usage to supply at that time.
  const bomRowBindings = useMemo(() => {
    if (!selectedFormulaUsage) return [];
    return (selectedFormulaUsage.input_bindings || [])
      .map((b) => {
        if (b.source_type !== 'mapped_value') return null;
        const mapping = domainCodes.find((m) => m.code === b.source_record_code);
        if (!mapping || mapping.value_model_label || mapping.model_label !== THIS_SCREEN_MODEL_LABEL) return null;
        return { binding: b, mapping };
      })
      .filter(Boolean);
  }, [selectedFormulaUsage, domainCodes]);
  // Everything else that needs a record — a Reference Value mapping
  // (Assay/water_content, off a Material Inventory Lot) or, in principle,
  // a Field-Path mapping to some other table entirely — is read-only here.
  // Whichever table it actually is, this screen doesn't already have its
  // rows loaded, so offering a picker would mean fetching just for this
  // one field; that record is most likely genuinely a per-execution fact
  // anyway (which lot, which batch, ...), so it's left for whoever runs
  // this formula to supply, same as it already works for Assay/water_content.
  const bomRuntimeBindings = useMemo(() => {
    if (!selectedFormulaUsage) return [];
    return (selectedFormulaUsage.input_bindings || [])
      .map((b) => {
        if (b.source_type !== 'mapped_value') return null;
        const mapping = domainCodes.find((m) => m.code === b.source_record_code);
        if (!mapping) return null;
        const neededModelLabel = mapping.value_model_label || mapping.model_label;
        if (!neededModelLabel || neededModelLabel === THIS_SCREEN_MODEL_LABEL) return null;
        return { binding: b, mapping, neededModelLabel, neededFieldName: mapping.value_column || mapping.field_name };
      })
      .filter(Boolean);
  }, [selectedFormulaUsage, domainCodes]);
  const [savingBomRowBinding, setSavingBomRowBinding] = useState(false);
  const saveBomRowBinding = (variableName, bomItemId) => {
    if (!selectedFormulaUsage) return;
    const bindingsPayload = (selectedFormulaUsage.input_bindings || []).map((b, idx) => ({
      variable_name: b.variable_name,
      source_type: b.source_type,
      required: b.required,
      fallback_policy: b.fallback_policy,
      expected_unit_code: b.source_type === 'mapped_value' ? '' : (b.expected_unit_code || ''),
      sequence: idx + 1,
      constant_value: b.source_type === 'constant' ? (b.constant_value ?? null) : null,
      default_value: b.fallback_policy === 'default' ? (b.default_value ?? null) : null,
      source_record_code: b.source_type === 'mapped_value' ? (b.source_record_code || '') : '',
      source_config:
        b.variable_name === variableName
          ? (bomItemId ? { id: bomItemId } : {})
          : b.source_type === 'mapped_value' || b.source_type === 'dependent_value'
            ? b.source_config || {}
            : {},
    }));
    setSavingBomRowBinding(true);
    update_formula_usage(
      selectedFormulaUsage.id,
      { input_bindings: bindingsPayload },
      () => {
        // Link this BOM item to the specific usage its bindings just came
        // from (correction_formula alone doesn't say which usage — see the
        // model field's docstring), and re-run the real calculation now
        // that this binding changed — a real save, not just this dialog's
        // local state, so corrected_quantity reflects it immediately
        // instead of waiting for an unrelated edit to trigger it.
        update_recipe_bom_item(
          (res2) => {
            setSavingBomRowBinding(false);
            if (res2?.status === 'success' || res2?.data?.status === 'success') {
              toast.success(bomItemId ? 'Saved which BOM row this reads from' : 'Cleared — left for the user to supply at run time');
            } else {
              toast.warning(res2?.message || res2?.data?.message || 'Saved the binding, but recalculation failed');
            }
            fetchFormulaUsages();
            fetchItems();
          },
          { bom_item_id: form.bom_item_id, correction_formula_usage_id: selectedFormulaUsage.id }
        );
      },
      (err) => {
        setSavingBomRowBinding(false);
        toast.error(formatFormulaTestError(err) || 'Failed to save');
      }
    );
  };
  // The row being edited is almost always the right default for its own
  // rule/std/corrected-quantity bindings (std_qty reads THIS ingredient's
  // own value far more often than not) — so rather than making the admin
  // pick "this ingredient" from the dropdown every time, auto-save it the
  // first time a binding shows up unset. Guarded so it only ever fires once
  // per binding per session — if the admin then explicitly clears it back
  // to "user selects at run time", that choice sticks and isn't re-forced.
  const autoDefaultedRowBindings = useRef(new Set());
  useEffect(() => {
    if (!showForm || !form.bom_item_id || !selectedFormulaUsage) return;
    const thisRowId = Number(form.bom_item_id);
    bomRowBindings.forEach(({ binding }) => {
      if (binding.source_config && binding.source_config.id) return;
      const key = `${selectedFormulaUsage.id}:${binding.variable_name}`;
      if (autoDefaultedRowBindings.current.has(key)) return;
      autoDefaultedRowBindings.current.add(key);
      saveBomRowBinding(binding.variable_name, thisRowId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm, form.bom_item_id, selectedFormulaUsage, bomRowBindings]);
  useEffect(() => {
    const materialInventoryId = form.material_inventory_id;
    if (!materialInventoryId || !testNeededValueModels.length) {
      setPickerFiltersForModel({});
      return undefined;
    }
    let cancelled = false;
    const seedLabel = 'djangoapp.bp_material_inventory';
    testNeededValueModels.forEach((targetLabel) => {
      get_formula_model_filter_path(targetLabel, seedLabel, materialInventoryId, (res) => {
        if (cancelled) return;
        const filters = res?.data?.data?.filters;
        setPickerFiltersForModel((prev) => ({ ...prev, [targetLabel]: filters || {} }));
      });
    });
    return () => {
      cancelled = true;
    };
  }, [form.material_inventory_id, testNeededValueModels]);
  const liveStdQty = useMemo(() => {
    if (form.entry_mode === 'target' && form.target_quantity !== '') return Number(form.target_quantity);
    if (QS_TYPES.includes(form.rule_type) && form.qs_std_quantity !== '') return Number(form.qs_std_quantity);
    return currentBomRow?.computed_quantity ?? null;
  }, [form.entry_mode, form.target_quantity, form.qs_std_quantity, form.rule_type, currentBomRow]);
  const liveCorrectedQty = useMemo(() => {
    if (isCorrectionPending(currentBomRow)) return null;
    return currentBomRow?.corrected_quantity ?? currentBomRow?.computed_quantity ?? liveStdQty;
  }, [currentBomRow, liveStdQty]);
  const bomValueOptions = useMemo(() => {
    const rows = items.filter((i) => !i.isGroupHeader);
    const currentLabel = form.material_inventory_id
      ? materialOptions.find((m) => String(m.value) === String(form.material_inventory_id))?.label
      : null;
    const opts = [];
    if (form.rule_value !== '' && form.rule_value !== null && form.rule_value !== undefined) {
      opts.push({
        value: '__current_rule_value',
        label: `${currentLabel || 'Current row'} · Rule Value (${formatRuleValue(form.rule_value, 3)})`,
      });
    }
    if (liveStdQty !== null && liveStdQty !== undefined && liveStdQty !== '') {
      opts.push({
        value: '__current_std_qty',
        label: `${currentLabel || 'Current row'} · Std Qty (${formatRuleValue(liveStdQty, 3)})`,
      });
    }
    if (liveCorrectedQty !== null && liveCorrectedQty !== undefined && liveCorrectedQty !== '') {
      opts.push({
        value: '__current_corrected_qty',
        label: `${currentLabel || 'Current row'} · Corrected Qty (${formatRuleValue(liveCorrectedQty, 3)})`,
      });
    }
    rows.forEach((row) => {
      if (row.rule_value !== null && row.rule_value !== undefined) {
        opts.push({
          value: `row:${row.bom_item_id}:rule`,
          label: `${row.material_name} · Rule Value (${formatRuleValue(row.rule_value, 3)})`,
        });
      }
      if (row.computed_quantity !== null && row.computed_quantity !== undefined) {
        opts.push({
          value: `row:${row.bom_item_id}:std`,
          label: `${row.material_name} · Std Qty (${formatRuleValue(row.computed_quantity, 3)})`,
        });
      }
      const effective = effectiveQty(row);
      if (effective !== null && effective !== undefined) {
        opts.push({
          value: `row:${row.bom_item_id}:corrected`,
          label: `${row.material_name} · Corrected Qty (${formatRuleValue(effective, 3)})`,
        });
      }
    });
    return opts;
  }, [items, form.material_inventory_id, materialOptions, form.rule_value, liveStdQty, liveCorrectedQty]);
  const resolveBomSourceValue = (sourceKey) => {
    if (!sourceKey) return null;
    if (sourceKey === '__current_rule_value') return form.rule_value !== '' ? Number(form.rule_value) : null;
    if (sourceKey === '__current_std_qty') return liveStdQty;
    if (sourceKey === '__current_corrected_qty') return liveCorrectedQty;
    if (!sourceKey.startsWith('row:')) return null;
    const [, rowId, field] = sourceKey.split(':');
    const row = items.find((i) => String(i.bom_item_id) === String(rowId));
    if (!row) return null;
    if (field === 'rule') return row.rule_value;
    if (field === 'std') return row.computed_quantity;
    if (field === 'corrected') return effectiveQty(row);
    return null;
  };
  const autoResolvableBindingOptions = useMemo(() => {
    const map = {};
    (selectedFormulaUsage?.input_bindings || []).forEach((binding) => {
      const needsBomSourcePick =
        binding.source_type === 'mapped_value' &&
        (!binding.source_config || !Object.keys(binding.source_config).length);
      if (!needsBomSourcePick) return;
      const lowerName = String(binding.variable_name || '').toLowerCase();
      let options = bomValueOptions;
      if (lowerName.includes('std')) options = bomValueOptions.filter((o) => String(o.value).includes(':std') || o.value === '__current_std_qty');
      else if (lowerName.includes('correct')) options = bomValueOptions.filter((o) => String(o.value).includes(':corrected') || o.value === '__current_corrected_qty');
      else if (lowerName.includes('rule') || lowerName.includes('value')) options = bomValueOptions.filter((o) => String(o.value).includes(':rule') || o.value === '__current_rule_value');
      map[binding.variable_name] = options;
    });
    return map;
  }, [selectedFormulaUsage, bomValueOptions]);
  // A BOM item's Value has no UOM of its own for most rule types — it's read
  // as already being expressed in the recipe's own Batch Size unit (see
  // bom_calculation.py: _round_to_recipe_unit), so show that unit as a fixed
  // suffix on the input instead of a separate picker. PER_VOLUME/PER_MASS are
  // the exception — they dose at a rate "per 1 [rule_uom]" with rule_uom a
  // real, pickable unit (see ruleUomOptions below).
  const recipeUnitName = units.find((u) => u.id === recipeUnitId)?.unit_name || '';
  const disabledRuleTypes = new Set([
    ...(perVolumeEnabled ? [] : ['PER_VOLUME']),
    ...(perMassEnabled ? [] : ['PER_MASS']),
    ...(qsTargetParameterEnabled ? [] : ['QS_TARGET_PARAMETER']),
  ]);
  const ruleTypeOptions = RULE_TYPE_OPTIONS.filter((o) => !disabledRuleTypes.has(o.value));

  const isQsRule = QS_TYPES.includes(form.rule_type);
  const showValue = !HIDE_VALUE_RULE_TYPES.includes(form.rule_type) && !isQsRule;
  const showVariant = PER_OUTPUT_UNIT_RULE_TYPES.includes(form.rule_type);
  const showReferenceBatch = form.rule_type === 'FIXED_BATCH_QTY';
  const showUomBasis = UOM_BASIS_RULE_TYPES.includes(form.rule_type);
  const showTargetParameter = form.rule_type === 'QS_TARGET_PARAMETER';
  const showReversibleToggle = showValue && REVERSIBLE_RULE_TYPES.includes(form.rule_type);
  // The reverse-solve target is always Std Qty — the pre-correction value,
  // the one you actually know a real number for (from a paper batch
  // record, say). Corrected Qty is an output, not something to type a
  // target for: it depends on the correction formula's other inputs too,
  // so there's no real number to aim for in advance. Std Qty forward-
  // solves the rule value; Corrected Qty is then calculated from THAT,
  // same as any other save (see _apply_correction).
  const targetQuantityLabel = 'Std Qty';
  const isPercentRule = PERCENT_RULE_TYPES.includes(form.rule_type);
  const ruleUomOptions = showUomBasis
    ? units.filter((u) => u.unit_category === RULE_TYPE_UNIT_CATEGORY[form.rule_type]).map((u) => ({ value: u.id, label: u.unit_name }))
    : [];
  const selectedRuleUomName = units.find((u) => u.id === form.rule_uom_id)?.unit_name || '';
  const valueAddon = isPercentRule
    ? '%'
    : showUomBasis
      ? (selectedRuleUomName ? `per ${selectedRuleUomName}` : undefined)
      : (recipeUnitName || undefined);

  // Same material can legitimately appear on more than one line (e.g. dosed
  // in two parts) — Variant only ever applies to PER_UNIT/FIXED_COUNT rows,
  // so hide that column entirely when nothing in this BOM uses it, instead
  // of showing an all-"—" column.
  const anyVariantUsed = items.some((i) => i.recipe_variant_id);

  // Rows are grouped into collapsible sections by group_label — the group
  // name becomes a header row (with a running material count) with its
  // members nested as children (antd Table's tree-data support renders the
  // expand/collapse arrow automatically); ungrouped items stay flat, exactly
  // as before grouping existed.
  const groupedData = (() => {
    const byGroup = new Map();
    const ungrouped = [];
    items.forEach((item) => {
      if (item.group_label) {
        if (!byGroup.has(item.group_label)) byGroup.set(item.group_label, []);
        byGroup.get(item.group_label).push(item);
      } else {
        ungrouped.push(item);
      }
    });
    const groupRows = [...byGroup.entries()].map(([label, children]) => ({
      bom_item_id: `group-${label}`,
      isGroupHeader: true,
      material_name: label,
      __childCount: children.length,
      children,
    }));
    return [...groupRows, ...ungrouped];
  })();

  // Group header rows show only the Material cell (spanning every other
  // column) — every other column collapses away (colSpan 0) rather than
  // rendering dashes for fields a header row doesn't have.
  // 7 original + Role, Overage %, Tolerance % (2026-09-01) = 10.
  const columnCount = 10 + (anyVariantUsed ? 1 : 0);
  const mergeAwayForGroupHeader = (record) => (record.isGroupHeader ? { colSpan: 0 } : {});

  const columns = [
    {
      title: 'Material',
      dataIndex: 'material_name',
      key: 'material_name',
      width: 220,
      onCell: (record) => (record.isGroupHeader ? { colSpan: columnCount } : {}),
      render: (v, r) =>
        r.isGroupHeader ? (
          <span className="font-semibold" style={{ color: accentColor }}>
            {v} <span className="text-gray-400 font-normal">({r.__childCount} item{r.__childCount === 1 ? '' : 's'})</span>
          </span>
        ) : (
          <Tooltip title={r.alias ? `${v} (${r.alias})` : v}>
            <span style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {v}
              {r.alias && <span className="text-gray-400"> ({r.alias})</span>}
            </span>
          </Tooltip>
        ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 110,
      onCell: mergeAwayForGroupHeader,
      render: (v) => (v ? <Tag color={ROLE_COLOR[v] || 'default'}>{ROLE_OPTIONS.find((o) => o.value === v)?.label || v}</Tag> : <span className="text-gray-400">—</span>),
    },
    ...(anyVariantUsed ? [{
      title: 'Variant',
      dataIndex: 'recipe_variant_name',
      key: 'recipe_variant_name',
      onCell: mergeAwayForGroupHeader,
      render: (v) => v || <span className="text-gray-400">—</span>,
    }] : []),
    {
      title: 'Std Qty',
      key: 'computed_quantity',
      align: 'right',
      onCell: mergeAwayForGroupHeader,
      render: (_, r) =>
        r.computed_quantity !== null && r.computed_quantity !== undefined ? (
          <span>{r.computed_quantity.toFixed(r.recipe_unit_decimal_places ?? 3)}{r.recipe_unit_name ? ` ${r.recipe_unit_name}` : ''}</span>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      // The Rule value that drives Std Qty forward — shown relative to
      // Std Qty since that's exactly what it is: this rule type's value
      // (%, per-unit, fixed) inverted off Std Qty. Stable, never
      // overwritten by a correction (see relative(Corrected) for the
      // other half of this same pair).
      title: 'Relative (Std)',
      key: 'relative_std',
      align: 'right',
      onCell: mergeAwayForGroupHeader,
      render: (_, r) => {
        if (!r.rule_type) return <span className="text-gray-400">—</span>;
        const label = RULE_TYPE_OPTIONS.find((o) => o.value === r.rule_type)?.label || r.rule_type;
        const isPercent = PERCENT_RULE_TYPES.includes(r.rule_type);
        const isUomBasis = UOM_BASIS_RULE_TYPES.includes(r.rule_type);
        const ruleValueText = formatRuleValue(r.rule_value, isPercent ? 2 : 3);
        // QS rows: just the badge, plus the target/rule value if one was
        // given — Std Qty already has its own column, no need to repeat it.
        const qtyText = HIDE_VALUE_RULE_TYPES.includes(r.rule_type) || r.rule_value == null
          ? null
          : isPercent
            ? `${ruleValueText}%`
            : isUomBasis
              ? `${ruleValueText} per ${r.rule_uom_name || '?'}`
              : `${ruleValueText}${r.recipe_unit_name ? ` ${r.recipe_unit_name}` : ''}`;
        const referenceText = r.reference_batch_quantity
          ? ` per ${r.reference_batch_quantity}${r.recipe_unit_name ? ` ${r.recipe_unit_name}` : ''} batch`
          : null;
        const targetParamText = r.rule_type === 'QS_TARGET_PARAMETER' && r.target_parameter_name
          ? ` — ${r.target_parameter_name}`
          : null;
        return (
          <div className="flex items-center justify-end gap-2">
            {qtyText && (
              <span className="font-medium">{qtyText}{referenceText}{targetParamText}</span>
            )}
            <Tag color="blue">{label}</Tag>
          </div>
        );
      },
    },
    {
      title: 'Corrected Qty',
      key: 'corrected_quantity',
      align: 'right',
      onCell: mergeAwayForGroupHeader,
      // No correction formula on a row means corrected == Std Qty (there's
      // nothing to correct) — shown as a fallback to Std Qty rather than a
      // dash, so this column always reflects the real, effective quantity.
      // 2026-09-01 (2nd pass): a row that DOES have a correction_formula but can't resolve it (a
      // binding like Assay/Water Content that only ever exists once a real batch picks a real Lot
      // — see the "User selects at run time" note in Edit BOM Item) will NEVER resolve at the
      // recipe/MFR level, only at actual batch execution — a separate, later, audited step. So
      // "Pending" forever here was misleading, not honest; the backend now saves Std Qty as a
      // placeholder in this case too (bom_calculation.py), same identity as "no formula at all".
      // isPlaceholder distinguishes the two ONLY for the small info-icon note below — the number
      // itself is correct either way.
      render: (_, r) => {
        if (isCorrectionPending(r)) return <span className="text-amber-600">{CORRECTION_PENDING_TEXT}</span>;
        const hasCorrection = r.corrected_quantity !== null && r.corrected_quantity !== undefined;
        const effective = hasCorrection ? r.corrected_quantity : r.computed_quantity;
        const isPlaceholder = !!(r.correction_formula_id || r.correction_formula_name)
          && effective !== null && effective !== undefined
          && r.computed_quantity !== null && r.computed_quantity !== undefined
          && Math.abs(effective - r.computed_quantity) < 1e-6;
        if (effective === null || effective === undefined) {
          return <span className="text-gray-400">—</span>;
        }
        return (
          <span className={hasCorrection && !isPlaceholder ? 'font-medium' : ''}>
            {effective.toFixed(r.recipe_unit_decimal_places ?? 3)}{r.recipe_unit_name ? ` ${r.recipe_unit_name}` : ''}
            {isPlaceholder && (
              <Tooltip title="Uses Std Qty for now — this material's correction formula depends on a real per-execution fact (e.g. a Lot's Assay/Water Content) that only exists once an actual batch runs, so it can't be applied yet at the recipe level. The real, audited correction happens then.">
                <InfoCircleOutlined style={{ marginLeft: 4, color: '#f59e0b' }} />
              </Tooltip>
            )}
          </span>
        );
      },
    },
    {
      // The other half of the pair — same rule type's value, inverted off
      // Corrected Qty instead: what this row's material actually
      // contributes once its correction formula is applied. Purely
      // derived (relative_rule_value in get_recipe_bom_item_list), never
      // stored, never feeds back into Std Qty or Rule.
      title: 'Relative (Corrected)',
      key: 'relative_corrected',
      align: 'right',
      onCell: mergeAwayForGroupHeader,
      render: (_, r) => {
        if (!r.rule_type || r.relative_rule_value == null) return <span className="text-gray-400">—</span>;
        // Same "placeholder, not a real correction yet" case as the Corrected Qty column — this is
        // derived FROM corrected_quantity, which the backend now falls back to Std Qty for when a
        // correction formula is attached but can only ever resolve at batch execution (see the
        // Corrected Qty column above). Same info-icon note here, same "the number is right, it's
        // just provisional" framing — never a blocking "Pending" tag.
        const isPlaceholder = !!(r.correction_formula_id || r.correction_formula_name)
          && r.corrected_quantity !== null && r.corrected_quantity !== undefined
          && r.computed_quantity !== null && r.computed_quantity !== undefined
          && Math.abs(r.corrected_quantity - r.computed_quantity) < 1e-6;
        const isPercent = PERCENT_RULE_TYPES.includes(r.rule_type);
        const isUomBasis = UOM_BASIS_RULE_TYPES.includes(r.rule_type);
        const text = isPercent
          ? `${formatRuleValue(r.relative_rule_value, 2)}%`
          : isUomBasis
            ? `${formatRuleValue(r.relative_rule_value, 3)} per ${r.rule_uom_name || '?'}`
            : `${formatRuleValue(r.relative_rule_value, 3)}${r.recipe_unit_name ? ` ${r.recipe_unit_name}` : ''}`;
        if (isPlaceholder) {
          return (
            <Tooltip title="Uses Relative (Std) for now — this material's correction formula depends on a real per-execution fact (e.g. a Lot's Assay/Water Content) that only exists once an actual batch runs. The real, audited correction happens then.">
              <span className="text-gray-500">
                {text} <InfoCircleOutlined style={{ color: '#f59e0b' }} />
              </span>
            </Tooltip>
          );
        }
        return (
          <Tooltip title="What this row's material actually contributes after its correction formula is applied — the same value, inverted off Corrected Qty instead of Std Qty.">
            <span className="text-gray-500">{text}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Correction Formula',
      key: 'correction_formula_name',
      width: 260,
      onCell: mergeAwayForGroupHeader,
      render: (_, r) =>
        r.correction_formula_name ? (
          <div className="flex flex-col gap-0.5" style={{ maxWidth: 260 }}>
            <span style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {r.correction_formula_name}
            </span>
            {r.correction_formula_expression && (
              <Tooltip title={r.correction_formula_expression}>
                <span
                  className="font-mono text-gray-500"
                  style={{ fontSize: 11, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  = {r.correction_formula_expression}
                </span>
              </Tooltip>
            )}
          </div>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      title: 'Overage %',
      dataIndex: 'overage_percent',
      width: 100,
      onCell: mergeAwayForGroupHeader,
      render: (v) => (v != null ? `${Number(v)}%` : <span className="text-gray-400">—</span>),
    },
    {
      title: 'Tolerance %',
      dataIndex: 'tolerance_percent',
      width: 100,
      onCell: mergeAwayForGroupHeader,
      render: (v) => (v != null ? `±${Number(v)}%` : <span className="text-gray-400">—</span>),
    },
    {
      title: 'Action',
      key: 'action',
      width: 90,
      align: 'center',
      onCell: mergeAwayForGroupHeader,
      render: (_, record) => (
        <div className="flex items-center justify-center gap-2">
          <Tooltip title="Edit">
            <Icons.Pencil onClick={() => handleEdit(record)} className="text-amber-600 w-4 h-4 cursor-pointer hover:text-amber-800" />
          </Tooltip>
          <Popconfirm title="Delete this BOM item?" onConfirm={() => handleDelete(record)} okText="Yes" cancelText="No">
            <Tooltip title="Delete">
              <Icons.Trash2 className="text-red-600 w-4 h-4 cursor-pointer hover:text-red-800" />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  // QS rows (Final Volume/Weight, Target Parameter, As Required) are never
  // resolved until batch execution — their Std Qty is just a saved reference
  // value, not a real allocation against the batch, so they're excluded from
  // every total below.
  const nonQsItems = items.filter((i) => !QS_TYPES.includes(i.rule_type));
  // Same "corrected where there's a correction, std otherwise" rule as
  // totalCorrectedQty below — a row's Rule % only reflects what it truly
  // contributes once its correction formula (if any) is accounted for.
  // Without this, a row whose corrected quantity is *less* than its Std
  // Qty (e.g. one ingredient's amount reduced by another's already-
  // corrected amount) makes the total read over 100% even though the
  // actual batch balances correctly — the raw Rule %'s were never meant
  // to be read on their own once a correction changes the outcome.
  const totalPercent = nonQsItems
    .filter((i) => PERCENT_RULE_TYPES.includes(i.rule_type) && i.rule_value != null)
    .reduce((sum, i) => sum + (i.relative_rule_value != null ? i.relative_rule_value : i.rule_value), 0);
  // The batch-size validation has to compare against what's actually
  // weighed/used, not the pre-correction Std Qty — so each row contributes
  // its Corrected Qty when a correction formula was applied, and falls back
  // to Std Qty when it wasn't (no formula means corrected == std, they're
  // the same number). This is also what "Corrected Qty" now displays per
  // row and totals to, above.
  // FRM-01 A4-2: a pending correction makes the total pending too — never a sum over Std Qty.
  const anyCorrectionPending = nonQsItems.some(isCorrectionPending);
  const totalCorrectedQty = anyCorrectionPending ? 0 : nonQsItems.reduce((sum, i) => {
    const effective = effectiveQty(i);
    return effective != null ? sum + effective : sum;
  }, 0);
  const summaryDecimalPlaces = items.find((i) => i.recipe_unit_decimal_places != null)?.recipe_unit_decimal_places ?? 3;
  const summaryUnitName = items.find((i) => i.recipe_unit_name)?.recipe_unit_name || '';
  // Validation only, never blocking: a fully percent-based BOM should sum to
  // 100%, and its total effective (corrected, or std where uncorrected)
  // quantity should line up with the recipe's own batch size, since that's
  // the basis the whole formula is computed against. A recipe that tops up
  // with a QS material (e.g. water to volume) will legitimately fall short
  // here — QS rows are excluded above — so this is a visual sanity flag, not
  // an error.
  const percentMismatch = !!totalPercent && Math.abs(totalPercent - 100) > 0.05;
  const qtyTolerance = recipeMasterQty ? Math.max(0.01, recipeMasterQty * 0.0005) : 0.01;
  const qtyMismatch = !!recipeMasterQty && !!totalCorrectedQty && Math.abs(totalCorrectedQty - recipeMasterQty) > qtyTolerance;
  // Difference expressed as a percent of the target, so a mismatch reads at
  // a glance regardless of unit/scale ("+2.86%" vs a raw kg delta).
  const percentDiffText = totalPercent ? `${totalPercent - 100 > 0 ? '+' : ''}${formatRuleValue(totalPercent - 100, 2)}%` : '';
  const qtyDiffPercent = recipeMasterQty ? ((totalCorrectedQty - recipeMasterQty) / recipeMasterQty) * 100 : 0;
  const qtyDiffText = `${qtyDiffPercent > 0 ? '+' : ''}${formatRuleValue(qtyDiffPercent, 2)}%`;
  const matchBg = isDark ? 'rgba(34,197,94,0.15)' : '#dcfce7';
  const matchColor = '#16a34a';
  const mismatchBg = isDark ? 'rgba(220,38,38,0.15)' : '#fee2e2';
  const mismatchColor = '#dc2626';
  const validationBadgeStyle = { padding: '2px 8px', borderRadius: 4, display: 'inline-block' };
  // Column order is: Material, Role, [Variant], Std Qty, relative(Std),
  // Corrected Qty, relative(Corrected), Correction Formula, Overage %,
  // Tolerance %, Action — these indices have to track that exactly for
  // AntTable.Summary to line up under the right header.
  // 2026-09-01: real bug — this summary row was built back when the column
  // order was just Material, [Variant], Std Qty, ...; it was never updated
  // when Role (then Overage %/Tolerance %) were added, so every cell from
  // Std Qty on rendered one column too far LEFT. The totals themselves were
  // already computed off corrected_quantity (see totalCorrectedQty/
  // totalPercent above) — it only ever LOOKED like a Std-based total because
  // it visually landed under "Relative (Std)"/"Corrected Qty" instead of
  // "Corrected Qty"/"Relative (Corrected)". Fixed by adding the missing
  // Role cell and the two missing trailing cells (Overage %, Tolerance %).
  const variantOffset = anyVariantUsed ? 1 : 0;
  const renderSummary = () => (
    <AntTable.Summary>
      <AntTable.Summary.Row style={{ fontWeight: 600, background: isDark ? defaultColors.palette.gray[700] : '#f8faff' }}>
        <AntTable.Summary.Cell index={0}>Total</AntTable.Summary.Cell>
        {/* Role: no total — a role isn't summable. */}
        <AntTable.Summary.Cell index={1} />
        {anyVariantUsed && <AntTable.Summary.Cell index={2} />}
        {/* Std Qty: no total here — it's a pre-correction intermediate
            value, not what a BOM should actually be validated against. */}
        <AntTable.Summary.Cell index={2 + variantOffset} />
        {/* relative(Std): no validated total either — the raw Rule %'s
            summing past 100% is expected once a correction subtracts one
            row's contribution from another's; only the corrected-aware
            total (below, under relative(Corrected)) is the real check. */}
        <AntTable.Summary.Cell index={3 + variantOffset} />
        <AntTable.Summary.Cell index={4 + variantOffset} align="right">
          {anyCorrectionPending ? (
            <span className="text-amber-600">{CORRECTION_PENDING_TEXT}</span>
          ) : totalCorrectedQty ? (
            <Tooltip title={
              !recipeMasterQty
                ? undefined
                : qtyMismatch
                  ? `Batch Size is ${recipeMasterQty.toFixed(summaryDecimalPlaces)} ${summaryUnitName} — off by ${qtyDiffText} (QS/fill items excluded here)`
                  : 'Matches Batch Size'
            }>
              <span style={recipeMasterQty ? { ...validationBadgeStyle, background: qtyMismatch ? mismatchBg : matchBg, color: qtyMismatch ? mismatchColor : matchColor } : undefined}>
                {totalCorrectedQty.toFixed(summaryDecimalPlaces)} {summaryUnitName}{recipeMasterQty && qtyMismatch ? ` (${qtyDiffText})` : ''}
              </span>
            </Tooltip>
          ) : '—'}
        </AntTable.Summary.Cell>
        <AntTable.Summary.Cell index={5 + variantOffset} align="right">
          {totalPercent ? (
            <Tooltip title={percentMismatch ? `Should total 100% — off by ${percentDiffText}` : 'Matches 100%'}>
              <span style={{ ...validationBadgeStyle, background: percentMismatch ? mismatchBg : matchBg, color: percentMismatch ? mismatchColor : matchColor }}>
                {formatRuleValue(totalPercent, 2)}%{percentMismatch ? ` (${percentDiffText})` : ''}
              </span>
            </Tooltip>
          ) : '—'}
        </AntTable.Summary.Cell>
        <AntTable.Summary.Cell index={6 + variantOffset} />
        {/* Overage %, Tolerance % (2026-09-01) — not summable, just keep the row's column count matching the real table's. */}
        <AntTable.Summary.Cell index={7 + variantOffset} />
        <AntTable.Summary.Cell index={8 + variantOffset} />
        <AntTable.Summary.Cell index={9 + variantOffset} />
      </AntTable.Summary.Row>
    </AntTable.Summary>
  );

  const openFormulaTest = () => {
    if (!form.correction_formula_id) {
      toast.warning('Select a correction formula first');
      return;
    }
    if (!matchingFormulaUsages.length) {
      toast.warning('No active formula usage is attached to this formula');
      return;
    }
    resetTestState();
    setSelectedUsageId(matchingFormulaUsages[0].id);
    setTestModalOpen(true);
  };

  const closeFormulaTest = () => {
    setTestModalOpen(false);
    resetTestState();
  };

  const openLookup = ({ modelLabel, title, target }) => {
    setLookupState({
      open: true,
      modelLabel,
      title,
      target,
    });
  };

  const closeLookup = () => {
    setLookupState({ open: false, modelLabel: '', title: '', target: null });
  };

  const applyLookupValue = (id, label) => {
    if (lookupState.target === 'batch') {
      setTestBatchId(id);
      setTestBatchLabel(label || '');
    } else if (lookupState.target?.kind === 'binding') {
      setTestValueRecordIdsByBinding((prev) => ({ ...prev, [lookupState.target.variableName]: id }));
    } else if (lookupState.target) {
      const modelLabel = lookupState.target;
      setTestValueRecordIds((prev) => ({ ...prev, [modelLabel]: id }));
      setTestValueRecordLabels((prev) => ({ ...prev, [modelLabel]: label || '' }));
    }
    closeLookup();
  };

  const runFormulaTest = () => {
    if (!selectedFormulaUsage) {
      toast.warning('Select a formula usage first');
      return;
    }
    const sampleOverrides = {};
    Object.entries(testOverrides).forEach(([key, val]) => {
      const binding = (selectedFormulaUsage.input_bindings || []).find((b) => b.variable_name === key);
      if (binding?.source_type !== 'dependent_value' && String(val).trim() !== '') sampleOverrides[key] = val;
    });
    Object.entries(testBomSources).forEach(([key, sourceKey]) => {
      if (sampleOverrides[key] !== undefined) return;
      const resolved = resolveBomSourceValue(sourceKey);
      if (resolved !== null && resolved !== undefined && resolved !== '') sampleOverrides[key] = resolved;
    });
    const dependentValues = {};
    Object.entries(testDependentValues).forEach(([key, val]) => {
      if (String(val).trim() !== '') dependentValues[key] = val;
    });
    const valueRecordIds = {};
    Object.entries(testValueRecordIds).forEach(([modelLabel, id]) => {
      if (id) valueRecordIds[modelLabel] = Number(id);
    });
    (selectedFormulaUsage.input_bindings || []).forEach((binding) => {
      if (binding.source_type !== 'mapped_value') return;
      if (binding.source_config && Object.keys(binding.source_config).length) return;
      const mapping = domainCodes.find((m) => m.code === binding.source_record_code);
      const modelLabel = mapping?.model_label;
      if (modelLabel && autoContextRecordIds[modelLabel] && !valueRecordIds[modelLabel]) {
        valueRecordIds[modelLabel] = autoContextRecordIds[modelLabel];
      }
    });
    const valueRecordIdsByBinding = {};
    Object.entries(testValueRecordIdsByBinding).forEach(([variableName, id]) => {
      if (id) valueRecordIdsByBinding[variableName] = Number(id);
    });
    // source_config_overrides: per-binding filter values (e.g. lot batch number) for a
    // "Reference Value by filter" mapping — merged into that binding's own source_config
    // server-side (registry.py._effective_source_config), just for this test run. Only send a
    // binding's entry once every discriminator column it needs actually has a value — a partial
    // set still fails the same "needs [...] set" check server-side, so there's nothing to gain
    // from sending it incomplete.
    const sourceConfigOverrides = {};
    (selectedFormulaUsage.input_bindings || []).forEach((binding) => {
      const mapping = domainCodes.find((m) => m.code === binding.source_record_code);
      const columns = mapping?.value_discriminator_columns || [];
      if (!columns.length) return;
      const values = testSourceConfigOverrides[binding.variable_name] || {};
      if (columns.every((c) => String(values[c] ?? '').trim() !== '')) {
        sourceConfigOverrides[binding.variable_name] = columns.reduce((acc, c) => ({ ...acc, [c]: values[c] }), {});
      }
    });
    const payload = {};
    if (Object.keys(sampleOverrides).length) payload.sample_overrides = sampleOverrides;
    if (Object.keys(dependentValues).length) payload.dependent_values = dependentValues;
    if (Object.keys(valueRecordIds).length) payload.value_record_ids = valueRecordIds;
    if (Object.keys(valueRecordIdsByBinding).length) payload.value_record_ids_by_binding = valueRecordIdsByBinding;
    if (Object.keys(sourceConfigOverrides).length) payload.source_config_overrides = sourceConfigOverrides;
    if (testNeedsBatchId && String(testBatchId).trim() !== '') payload.batch_id = Number(testBatchId);
    setTestingFormula(true);
    setTestResult(null);
    test_formula_usage(
      selectedFormulaUsage.id,
      payload,
      (data) => {
        setTestingFormula(false);
        setTestResult({ ok: true, data });
      },
      (err) => {
        setTestingFormula(false);
        setTestResult({ ok: false, error: formatFormulaTestError(err) || 'Formula test failed' });
      }
    );
  };

  useEffect(() => {
    if (!selectedFormulaUsage) return;
    const singleSourceSelections = {};
    Object.entries(autoResolvableBindingOptions).forEach(([variableName, options]) => {
      if (options.length === 1) singleSourceSelections[variableName] = options[0].value;
    });
    if (Object.keys(singleSourceSelections).length) {
      setTestBomSources((prev) => ({ ...singleSourceSelections, ...prev }));
    }
  }, [selectedFormulaUsage, autoResolvableBindingOptions]);

  useEffect(() => {
    if (!testModalOpen || !testNeededValueModels.length) return;
    testNeededValueModels.forEach((modelLabel) => {
      if (testValueRecordIds[modelLabel]) return;
      // "different" mode shows one Lookup per binding instead of this
      // shared box (see the render below) — nothing here to auto-narrow or
      // auto-open for a box that isn't even on screen.
      const groupBindings = testValueModelBindings[modelLabel] || [];
      const recordMode = testRecordMode[modelLabel] || (groupBindings.length > 1 ? 'different' : 'same');
      if (recordMode === 'different' && groupBindings.length > 1) return;
      get_formula_mappable_model_rows(
        modelLabel,
        '',
        (res) => {
          const payload = res?.data?.data || {};
          const rows = payload.rows || [];
          const columns = payload.columns || [];
          if (rows.length === 1) {
            // Exactly one candidate under the BOM's own context filters —
            // no ambiguity, just use it.
            setTestValueRecordIds((prev) => ({ ...prev, [modelLabel]: rows[0].id }));
            setTestValueRecordLabels((prev) => ({ ...prev, [modelLabel]: rowDisplayLabel(rows[0], columns) }));
            setTestValueCandidates((prev) => ({ ...prev, [modelLabel]: 0 }));
          } else if (rows.length > 1) {
            // More than one candidate — can't guess. Send the tester
            // straight to the real Lookup popup (real rows, real column
            // headers, including extraColumnsForModel) instead of a home-
            // grown dropdown — opened automatically the first time this
            // model turns out ambiguous, so there's nothing extra to click
            // through first.
            setTestValueCandidates((prev) => ({ ...prev, [modelLabel]: rows.length }));
            if (!autoOpenedForModel.current.has(modelLabel) && !lookupState.open) {
              autoOpenedForModel.current.add(modelLabel);
              openLookup({
                modelLabel,
                title: `Pick a ${capitalize(modelVerboseNames[modelLabel]) || 'record'} — ${rows.length} match${rows.length === 1 ? '' : 'es'} found`,
                target: modelLabel,
              });
            }
          } else {
            setTestValueCandidates((prev) => ({ ...prev, [modelLabel]: 0 }));
          }
        },
        pickerFiltersForModel[modelLabel] || {},
        extraColumnsForModel[modelLabel] || []
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testModalOpen, testNeededValueModels, testValueRecordIds, pickerFiltersForModel, extraColumnsForModel, testValueModelBindings, testRecordMode]);

  // 2026-09-01: "since I left them as user selects they will not be resolved now, but in test it
  // checks and auto resolved — then it should use for formula test only, not real value though."
  // Once the effect above auto-resolves (or the tester picks) a record for a Reference-Value-by-
  // filter binding's value_model_label, pre-fill its own discriminator columns from THAT record —
  // test-only (testSourceConfigOverrides never touches the binding's real, still-empty
  // source_config, which correctly stays "user selects at run time" in the database) — instead of
  // leaving the tester to retype values the app already just looked up moments ago.
  useEffect(() => {
    if (!testModalOpen || !selectedFormulaUsage) return;
    (selectedFormulaUsage.input_bindings || []).forEach((binding) => {
      const mapping = domainCodes.find((m) => m.code === binding.source_record_code);
      const columns = mapping?.value_discriminator_columns || [];
      if (!columns.length || !mapping.value_model_label) return;
      const recordId = testValueRecordIds[mapping.value_model_label];
      if (!recordId) return;
      const existing = testSourceConfigOverrides[binding.variable_name] || {};
      if (columns.every((c) => String(existing[c] ?? '').trim() !== '')) return;
      get_formula_model_field_values(mapping.value_model_label, recordId, columns, (res) => {
        const values = res?.data?.data || {};
        setTestSourceConfigOverrides((prev) => ({
          ...prev,
          [binding.variable_name]: { ...(prev[binding.variable_name] || {}), ...values },
        }));
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testModalOpen, selectedFormulaUsage, domainCodes, testValueRecordIds]);

  return (
    <div className="mt-3">
      <DerivedBom recipeId={recipeId} textColor={textColor} onLoaded={setHasDerived} batchSize={batchSize} batchUom={batchUom} massUom={massUom} />
      {hasDerived && !items.length && !showForm ? null : (<>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold" style={{ color: textColor }}>BOM</span>
        <Tooltip title={showForm ? 'Close' : 'Add BOM Item'}>
          {showForm ? (
            <Icons.X className="w-6 h-6 cursor-pointer text-red-500 hover:scale-110 transition-transform" onClick={resetForm} />
          ) : (
            <Icons.Plus className="w-6 h-6 cursor-pointer hover:scale-110 transition-transform" onClick={() => setShowForm(true)} />
          )}
        </Tooltip>
      </div>

      {/* 2026-09-01: "use popup for add and edit so we can use in proper space and widths
          properly ... popup should work similar to what we have in MBR" — a real Modal (960px,
          its own overlay/backdrop, centered on the viewport) instead of an inline panel squeezed
          into whatever width the enclosing page happens to have (e.g. the 860px MFR drawer). */}
      <Modal open={showForm} onCancel={resetForm} width={960} footer={null} destroyOnHidden
        title={form.bom_item_id ? 'Edit BOM item' : 'Add BOM item'}>
        <div
          className="rounded-xl border mb-4"
          style={{
            backgroundColor: formBgColor,
            borderColor: accentColor,
            borderLeftWidth: 4,
            borderLeftStyle: 'solid',
            padding: '18px 20px 12px',
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-md" style={{ backgroundColor: accentBg }}>
              <Icons.Plus className="w-4 h-4" style={{ color: accentColor }} />
            </div>
            <span className="text-sm font-semibold" style={{ color: accentColor }}>
              {form.bom_item_id ? 'Edit BOM Item' : 'Add BOM Item'}
            </span>
          </div>
          <Row gutter={[12, 12]}>
            <Col span={10}>
              <Select
                size="large"
                mode="tags"
                maxCount={1}
                placeholder="Group (optional — e.g. Part A, Aqueous Phase)"
                style={{ width: '100%' }}
                value={form.group_label ? [form.group_label] : []}
                onChange={(vals) => setForm({ ...form, group_label: vals[vals.length - 1] || '' })}
                options={existingGroupOptions}
              />
            </Col>
          </Row>
          {showReversibleToggle && (
            <Row gutter={[12, 12]} className="mt-2">
              <Col span={7} />
              <Col span={5} />
              <Col span={6}>
                <Radio.Group
                  size="small"
                  value={form.entry_mode}
                  onChange={(e) => setForm({ ...form, entry_mode: e.target.value, rule_value: '', target_quantity: '' })}
                >
                  <Radio.Button value="value">Value</Radio.Button>
                  <Radio.Button value="target">{targetQuantityLabel}</Radio.Button>
                </Radio.Group>
              </Col>
            </Row>
          )}
          <Row gutter={[12, 12]} align="middle" className="mt-2">
            {/* Role first, then material: the role decides which materials are on offer, so
                asking for it second meant picking from a list that had not been narrowed yet.
                Changing the role drops the material with it — one chosen under a manufacturing
                role is not on the packaging list, and vice versa. */}
            <Col span={5}>
              <Select
                size="large"
                placeholder="Role (API, Excipient, ...)"
                style={{ width: '100%' }}
                allowClear
                value={form.role || undefined}
                onChange={(val) => setForm({ ...form, role: val ?? null, material_inventory_id: null })}
                options={ROLE_OPTIONS}
              />
            </Col>
            <Col span={7}>
              {/* The one field this form cannot be saved without — `Recipe_BOM_Item` has no
                  null material and handleSave refuses before it sends. This form labels its
                  inputs with placeholders rather than <Form.Item label>, so the mark goes in
                  the placeholder: it is the only text the field shows. Nothing else here is
                  marked, because nothing else is actually required — a BOM line with just a
                  material is a line the server accepts. */}
              <Select
                size="large"
                placeholder={form.role ? '* Material' : '* Material (pick a role first)'}
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="label"
                value={form.material_inventory_id || undefined}
                onChange={(val) => setForm({ ...form, material_inventory_id: val })}
                options={materialOptions}
                notFoundContent={PACKAGING_ROLES.includes(form.role)
                  ? 'No packaging materials in this plant'
                  : 'No materials in this plant'}
              />
            </Col>
            <Col span={5}>
              <Input
                size="large"
                placeholder="Alias (optional)"
                value={form.alias}
                onChange={(e) => setForm({ ...form, alias: e.target.value })}
              />
            </Col>
            {showValue && (
              <Col span={6}>
                {showReversibleToggle && form.entry_mode === 'target' ? (
                  <InputNumber
                    size="large"
                    placeholder={`Target ${targetQuantityLabel}`}
                    addonAfter={recipeUnitName || undefined}
                    style={{ width: '100%' }}
                    value={form.target_quantity}
                    onChange={(val) => setForm({ ...form, target_quantity: val ?? '' })}
                  />
                ) : (
                  <InputNumber
                    size="large"
                    placeholder="Value"
                    addonAfter={valueAddon}
                    style={{ width: '100%' }}
                    value={form.rule_value}
                    precision={isPercentRule ? 2 : 3}
                    onChange={(val) => setForm({ ...form, rule_value: val ?? '' })}
                  />
                )}
              </Col>
            )}
            <Col span={showValue ? 6 : 12}>
              <Select
                size="large"
                placeholder="Rule Type"
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="label"
                value={form.rule_type || undefined}
                onChange={(val) => setForm({
                  ...form, rule_type: val, recipe_variant_id: null, reference_batch_quantity: '',
                  rule_uom_id: null, target_parameter_id: null, entry_mode: 'value', target_quantity: '',
                  qs_std_quantity: QS_TYPES.includes(val) ? form.qs_std_quantity : '',
                })}
                options={ruleTypeOptions}
                allowClear
              />
            </Col>
            {showReferenceBatch && (
              <Col span={24}>
                <InputNumber
                  size="large"
                  placeholder="Reference Batch Size (optional — makes Fixed scale with batch size)"
                  addonAfter={recipeUnitName || undefined}
                  style={{ width: '100%' }}
                  value={form.reference_batch_quantity}
                  onChange={(val) => setForm({ ...form, reference_batch_quantity: val ?? '' })}
                />
              </Col>
            )}
            {showUomBasis && (
              <Col span={24}>
                <Select
                  size="large"
                  placeholder={`Per (${RULE_TYPE_UNIT_CATEGORY[form.rule_type] === 'VOLUME' ? 'volume' : 'weight'} unit — e.g. mL, L or mg, g, kg)`}
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="label"
                  value={form.rule_uom_id || undefined}
                  onChange={(val) => setForm({ ...form, rule_uom_id: val })}
                  options={ruleUomOptions}
                  allowClear
                />
              </Col>
            )}
          </Row>
          {isQsRule && (
            <Row gutter={[12, 12]} align="middle" className="mt-2">
              <Col span={12}>
                <InputNumber
                  size="large"
                  placeholder="Rule / Target (optional — reference only, not calculated here)"
                  addonAfter={recipeUnitName || undefined}
                  style={{ width: '100%' }}
                  value={form.rule_value}
                  onChange={(val) => setForm({ ...form, rule_value: val ?? '' })}
                />
              </Col>
              <Col span={12}>
                <InputNumber
                  size="large"
                  placeholder="Std Qty (optional — reference only, not calculated here)"
                  addonAfter={recipeUnitName || undefined}
                  style={{ width: '100%' }}
                  value={form.qs_std_quantity}
                  onChange={(val) => setForm({ ...form, qs_std_quantity: val ?? '' })}
                />
              </Col>
            </Row>
          )}
          <Row gutter={[12, 12]} align="middle" className="mt-3">
            {showVariant && (
              <Col span={8}>
                <Select
                  size="large"
                  placeholder="Variant"
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="label"
                  value={form.recipe_variant_id || undefined}
                  onChange={(val) => setForm({ ...form, recipe_variant_id: val })}
                  options={variantOptions}
                  allowClear
                />
              </Col>
            )}
            {showTargetParameter && (
              <Col span={8}>
                <Select
                  size="large"
                  placeholder="Target Parameter (e.g. pH, Concentration, Weight)"
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="label"
                  value={form.target_parameter_id || undefined}
                  onChange={(val) => setForm({ ...form, target_parameter_id: val })}
                  options={parameterOptions}
                  allowClear
                />
              </Col>
            )}
            <Col span={8}>
              <Select
                size="large"
                placeholder="Correction Formula (optional)"
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="label"
                value={form.correction_formula_id || undefined}
                onChange={(val) => setForm({ ...form, correction_formula_id: val })}
                options={formulaOptions}
                allowClear
              />
            </Col>
            <Col span={4}>
              <InputNumber
                size="large"
                placeholder="Overage %"
                addonAfter="%"
                style={{ width: '100%' }}
                value={form.overage_percent}
                onChange={(val) => setForm({ ...form, overage_percent: val ?? '' })}
              />
            </Col>
            <Col span={4}>
              <InputNumber
                size="large"
                placeholder="Tolerance %"
                addonAfter="%"
                style={{ width: '100%' }}
                value={form.tolerance_percent}
                onChange={(val) => setForm({ ...form, tolerance_percent: val ?? '' })}
              />
            </Col>
            <Col flex="auto" />
            <Col>
              <div className="flex gap-2">
                <Tooltip title="Reset">
                  <Button size="large" icon={<Icons.Undo2 className="w-4 h-4" />} onClick={resetForm} />
                </Tooltip>
                <Button size="large" type="primary" icon={<Icons.Plus className="w-4 h-4" />} loading={saving} onClick={handleSave}>
                  {form.bom_item_id ? 'Update' : 'Add'}
                </Button>
              </div>
            </Col>
          </Row>
          {/* 2026-10-09 · the correction formula has a section of its own: the formula and (when it
              reads other BOM rows) which row each value comes from. Testing a formula is done on the
              Formula Library page before it is approved; the ingredient form only picks a verified one. */}
          {form.correction_formula_id && (
            <div
              className="mt-3 rounded-lg border p-3"
              style={{ borderColor: accentColor, background: formBgColor }}
              data-testid="bom-formula-section"
            >
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-sm font-semibold" style={{ color: accentColor }}>Correction formula</span>
                {selectedFormulaExpression && (
                  <span className="font-mono" style={{ fontSize: 12.5, color: '#64748b' }} title={selectedFormulaExpression}>
                    = {selectedFormulaExpression}
                  </span>
                )}
                {/* Only verified (Active) formulas are offered here; a formula is tested on the
                    Formula Library page, before it is approved — not while adding an ingredient. */}
                <span style={{ marginLeft: 'auto', fontSize: 12.5, color: '#389e0d' }}>✓ Verified formula</span>
              </div>
              {(bomRowBindings.length > 0 || bomRuntimeBindings.length > 0) ? (<>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-sm font-semibold" style={{ color: accentColor }}>
                  Which BOM row should each value read from?
                </span>
              </div>
              <div className="text-xs text-gray-500 mb-2">
                Fix a row now (it stays correct forever — a recipe's BOM doesn't change per batch), or leave it
                blank to require the user to supply it whenever this formula runs.
              </div>
              <div className="flex flex-col gap-2">
                {bomRowBindings.map(({ binding, mapping }) => {
                  const currentBomItemId = binding.source_config?.id ? Number(binding.source_config.id) : null;
                  const thisRowId = form.bom_item_id ? Number(form.bom_item_id) : null;
                  const rowOptions = items
                    .filter((i) => !i.isGroupHeader)
                    .map((i) => {
                      const itemId = i.bom_item_id ?? i.id;
                      return {
                        value: itemId,
                        label: itemId === thisRowId ? `${i.material_name} (this ingredient)` : i.material_name,
                      };
                    });
                  return (
                    <Row key={binding.id || binding.variable_name} gutter={12} align="middle">
                      <Col span={8}>
                        <div className="font-medium">{binding.variable_name}</div>
                        <div className="text-xs text-gray-500">
                          reads {mapping.field_name} off {capitalize(modelVerboseNames[mapping.model_label]) || 'Recipe BOM Item'}
                        </div>
                      </Col>
                      <Col span={12}>
                        <Select
                          size="large"
                          style={{ width: '100%' }}
                          showSearch
                          optionFilterProp="label"
                          allowClear
                          placeholder="Leave for the user to select at run time"
                          value={currentBomItemId || undefined}
                          options={rowOptions}
                          loading={savingBomRowBinding}
                          onChange={(val) => saveBomRowBinding(binding.variable_name, val || null)}
                        />
                      </Col>
                      <Col span={4}>
                        {currentBomItemId ? (
                          <Tag color="blue">Set</Tag>
                        ) : (
                          <Tooltip title="Left blank on purpose — pick a row above to fix it, or leave it for the user to supply whenever this formula runs. Until it's set, this material's Corrected Qty in the BOM table falls back to Std Qty (flagged with an info icon there); Update above still saves everything else fine.">
                            <Tag>
                              User selects at run time <InfoCircleOutlined style={{ marginLeft: 4 }} />
                            </Tag>
                          </Tooltip>
                        )}
                      </Col>
                    </Row>
                  );
                })}
                {bomRuntimeBindings.map(({ binding, neededModelLabel, neededFieldName }) => {
                  const friendlyName = capitalize(modelVerboseNames[neededModelLabel]) || 'a record';
                  return (
                    <Row key={binding.id || binding.variable_name} gutter={12} align="middle">
                      <Col span={8}>
                        <div className="font-medium">{binding.variable_name}</div>
                        <div className="text-xs text-gray-500">
                          reads {neededFieldName} off {friendlyName}
                        </div>
                      </Col>
                      <Col span={12}>
                        <div className="text-xs text-gray-500">
                          Which {friendlyName} is used isn&apos;t something this screen already has loaded — most likely a
                          real per-execution fact anyway (which lot, which batch, ...), so it&apos;s supplied when the
                          formula actually runs.
                        </div>
                      </Col>
                      <Col span={4}>
                        <Tooltip title="Can't be fixed to one record here — which Lot gets used is a real per-execution fact, only known once an actual batch runs and picks one. Until then, this material's Corrected Qty in the BOM table falls back to Std Qty (flagged with an info icon there); Update above still saves everything else fine.">
                          <Tag>
                            User selects at run time <InfoCircleOutlined style={{ marginLeft: 4 }} />
                          </Tag>
                        </Tooltip>
                      </Col>
                    </Row>
                  );
                })}
              </div>
              </>) : (
                <div className="text-xs text-gray-500">This formula reads no value from another BOM row — nothing to map.</div>
              )}
            </div>
          )}
        </div>
      </Modal>

      <TableContainer
        data={groupedData}
        columns={columns}
        size="small"
        bordered
        rowKey="bom_item_id"
        scrollX="max-content"
        height={null}
        pagination={false}
        expandable={{ childrenColumnName: 'children', defaultExpandAllRows: true }}
        summary={items.length ? renderSummary : undefined}
        locale={{ emptyText: 'No BOM items yet — add materials and their dosing rule here' }}
      />
      </>)}

      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold" style={{ color: textColor }}>Notes</span>
          {bomNotes !== savedBomNotes && (
            <Button type="primary" size="small" loading={savingNotes} onClick={handleSaveNotes}>
              Save Notes
            </Button>
          )}
        </div>
        <Input.TextArea
          placeholder="Any important remarks for this BOM — e.g. why a material is split, an assay-based adjustment, a substitution note..."
          autoSize={{ minRows: 2, maxRows: 6 }}
          value={bomNotes}
          onChange={(e) => setBomNotes(e.target.value)}
        />
      </div>

      <Modal
        open={testModalOpen}
        title="Test Correction Formula"
        onCancel={closeFormulaTest}
        footer={null}
        width={820}
        destroyOnHidden
      >
        <div className="flex flex-col gap-3">
          <Alert
            type="info"
            showIcon
            message="Run the selected formula usage here before saving the BOM line. This checks the Formula Usage path, not just the raw expression."
          />
          {matchingFormulaUsages.length > 1 && (
            <Select
              size="large"
              value={selectedFormulaUsage?.id}
              options={usageOptions}
              onChange={(val) => {
                setSelectedUsageId(val);
                setTestOverrides({});
                setTestDependentValues({});
                setTestBatchId('');
                setTestResult(null);
              }}
              placeholder="Select formula usage"
            />
          )}
          {selectedFormulaUsage && (
            <>
              <div className="rounded-lg border p-3" style={{ borderColor: '#dbe5f0', background: '#f8fbff' }}>
                <div className="font-semibold">{selectedFormulaUsage.name || 'Unnamed Usage'}</div>
                <div className="text-sm text-gray-500 mt-1">
                  Trigger: {selectedFormulaUsage.trigger_type || 'manual'} | Target: {selectedFormulaUsage.target_type || 'n/a'}
                </div>
              </div>
              <Row gutter={[16, 16]}>
                {testNeedsBatchId && (
                  <Col xs={24} md={12}>
                    <div
                      style={{
                        border: '1px solid #dbe5f0',
                        borderRadius: 14,
                        padding: 12,
                        background: '#f8fbff',
                      }}
                    >
                      {testBatchLabel && String(testBatchId).trim() !== '' ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                          <span style={{ fontSize: 13 }}>
                            Resolved: <strong style={{ color: '#0f172a' }}>{testBatchLabel}</strong>
                          </span>
                          <Button
                            size="small"
                            onClick={() => {
                              setTestBatchId('');
                              setTestBatchLabel('');
                            }}
                          >
                            Change
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div
                            style={{
                              color: '#64748b',
                              fontSize: 12,
                              marginBottom: 10,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            Which batch to resolve `$batch...` filters from
                          </div>
                          <div style={{ display: 'flex' }}>
                            <InputNumber
                              size="large"
                              style={{ width: '100%' }}
                              value={testBatchId}
                              onChange={(v) => {
                                setTestBatchId(v ?? '');
                                setTestBatchLabel('');
                              }}
                              placeholder="e.g. 1024"
                            />
                            <Button
                              size="large"
                              onClick={() =>
                                openLookup({
                                  modelLabel: 'djangoapp.batch_config',
                                  title: 'Pick a Batch',
                                  target: 'batch',
                                })
                              }
                            >
                              Lookup
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  </Col>
                )}
                {testNeededValueModels.map((modelLabel) => {
                  const candidateCount = testValueCandidates[modelLabel] || 0;
                  const isAmbiguous = candidateCount > 1 && !testValueRecordIds[modelLabel];
                  const friendlyName = capitalize(modelVerboseNames[modelLabel]) || 'record';
                  const resolvedLabel = testValueRecordLabels[modelLabel];
                  const hasResolvedLabel = resolvedLabel && String(testValueRecordIds[modelLabel] ?? '').trim() !== '';
                  const openThisLookup = () =>
                    openLookup({
                      modelLabel,
                      title: `Pick a ${friendlyName}${isAmbiguous ? ` — ${candidateCount} matches found` : ''}`,
                      target: modelLabel,
                    });
                  // Two bindings can share a value-model but need two
                  // different rows (e.g. a "dependent material" reading and
                  // "this material"'s own reading, both off
                  // djangoapp.recipe_bom_item) — the box below forces the
                  // SAME row onto every binding sharing modelLabel, so a
                  // group of more than one defaults to "different" and lets
                  // the tester switch back if a shared row really is right.
                  const groupBindings = testValueModelBindings[modelLabel] || [];
                  const recordMode = testRecordMode[modelLabel] || (groupBindings.length > 1 ? 'different' : 'same');
                  if (recordMode === 'different' && groupBindings.length > 1) {
                    return (
                      <Col xs={24} key={modelLabel}>
                        <div
                          style={{
                            border: '1px solid #dbe5f0',
                            borderRadius: 14,
                            padding: 12,
                            background: '#f8fbff',
                          }}
                        >
                          <Segmented
                            size="small"
                            style={{ marginBottom: 10 }}
                            value={recordMode}
                            onChange={(val) => setTestRecordMode((prev) => ({ ...prev, [modelLabel]: val }))}
                            options={[
                              { label: 'Same record for all', value: 'same' },
                              { label: 'Different record per variable', value: 'different' },
                            ]}
                          />
                          <div className="flex flex-col gap-2">
                            {groupBindings.map((binding) => (
                              <div key={binding.variable_name}>
                                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                                  {binding.display_name || binding.variable_name}
                                </div>
                                <div style={{ display: 'flex' }}>
                                  <InputNumber
                                    size="large"
                                    style={{ width: '100%' }}
                                    value={testValueRecordIdsByBinding[binding.variable_name] ?? ''}
                                    onChange={(v) =>
                                      setTestValueRecordIdsByBinding((prev) => ({ ...prev, [binding.variable_name]: v ?? '' }))
                                    }
                                    placeholder={`Pick ${friendlyName} ID`}
                                  />
                                  <Button
                                    size="large"
                                    onClick={() =>
                                      openLookup({
                                        modelLabel,
                                        title: `Pick a ${friendlyName} for ${binding.display_name || binding.variable_name}`,
                                        target: { kind: 'binding', variableName: binding.variable_name },
                                      })
                                    }
                                  >
                                    Lookup
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </Col>
                    );
                  }
                  return (
                    <Col xs={24} md={12} key={modelLabel}>
                      <div
                        style={{
                          border: '1px solid #dbe5f0',
                          borderRadius: 14,
                          padding: 12,
                          background: '#f8fbff',
                        }}
                      >
                        {groupBindings.length > 1 && (
                          <Segmented
                            size="small"
                            style={{ marginBottom: 10 }}
                            value={recordMode}
                            onChange={(val) => setTestRecordMode((prev) => ({ ...prev, [modelLabel]: val }))}
                            options={[
                              { label: 'Same record for all', value: 'same' },
                              { label: 'Different record per variable', value: 'different' },
                            ]}
                          />
                        )}
                        {hasResolvedLabel ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                            <span style={{ fontSize: 13 }}>
                              Resolved: <strong style={{ color: '#0f172a' }}>{resolvedLabel}</strong>
                            </span>
                            <Button
                              size="small"
                              onClick={() => {
                                setTestValueRecordIds((prev) => ({ ...prev, [modelLabel]: '' }));
                                setTestValueRecordLabels((prev) => ({ ...prev, [modelLabel]: '' }));
                              }}
                            >
                              Change
                            </Button>
                          </div>
                        ) : isAmbiguous ? (
                          <>
                            <div style={{ color: '#64748b', fontSize: 12, marginBottom: 10 }}>
                              Found {candidateCount} matching {friendlyName}s — pick one
                            </div>
                            <Button type="primary" size="large" style={{ width: '100%' }} onClick={openThisLookup}>
                              Select a {friendlyName}
                            </Button>
                          </>
                        ) : (
                          <>
                            <div
                              style={{
                                color: '#64748b',
                                fontSize: 12,
                                marginBottom: 10,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              Which record to resolve the mapped value(s) from
                            </div>
                            <div style={{ display: 'flex' }}>
                              <InputNumber
                                size="large"
                                style={{ width: '100%' }}
                                value={testValueRecordIds[modelLabel] ?? ''}
                                onChange={(v) => {
                                  setTestValueRecordIds((prev) => ({ ...prev, [modelLabel]: v ?? '' }));
                                  setTestValueRecordLabels((prev) => ({ ...prev, [modelLabel]: '' }));
                                }}
                                placeholder={`Pick ${friendlyName} ID`}
                              />
                              <Button size="large" onClick={openThisLookup}>
                                Lookup
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    </Col>
                  );
                })}
              </Row>
              {(selectedFormulaUsage.input_bindings || []).map((binding) => {
                const dependentKey = (binding.source_config || {}).dependent_key || binding.variable_name;
                const mapping = domainCodes.find((m) => m.code === binding.source_record_code);
                // A binding is already resolved by a *record*, not a BOM
                // table value, in two cases: (1) its model is one we know
                // directly from context (this BOM row / this recipe / the
                // chosen material inventory — autoContextRecordIds), or (2)
                // it's a Reference Value or context-anchored mapping (e.g.
                // Assay/water_content reading off Material Inventory Lot's
                // quality-parameter values) — that record is whatever the
                // matching box in testNeededValueModels above resolved
                // (auto-picked, disambiguated, or looked up), same source
                // as autoContextRecordIds either way. Either way, "pick a
                // value from the BOM table" is the wrong fallback for it —
                // that picker is only for bindings reading this BOM row's
                // OWN rule/std/corrected quantity.
                const autoContextLabel = mapping?.model_label && autoContextRecordIds[mapping.model_label]
                  ? mapping.model_label
                  : null;
                const recordModelLabel = mapping?.value_model_label || (mapping?.needs_context_anchor ? mapping.model_label : null);
                // 2026-09-01: "why is it checking the actual value though" — a mapping with
                // value_discriminator_columns set (e.g. Assay/water_content off a Material
                // Inventory Lot, keyed by batch/lot) resolves through
                // _resolve_reference_value_by_filter, which NEVER reads value_record_ids — no
                // record picked in "Same record for all" above can ever satisfy it, so it must
                // never count as "resolved" here (that was silently hiding its own input
                // entirely). It needs its own filter inputs (below), not the BOM-value picker or
                // record-pick shortcuts meant for other binding shapes.
                const needsDiscriminatorFilters = !!(mapping?.value_discriminator_columns || []).length;
                const isRecordResolved = !needsDiscriminatorFilters &&
                  !!(autoContextLabel || (recordModelLabel && testValueRecordIds[recordModelLabel]));
                const needsBomSourcePick =
                  !needsDiscriminatorFilters &&
                  binding.source_type === 'mapped_value' &&
                  (!binding.source_config || !Object.keys(binding.source_config).length) &&
                  !isRecordResolved;
                const filteredBomValueOptions = autoResolvableBindingOptions[binding.variable_name] || bomValueOptions;
                const autoSelectedBomSource = filteredBomValueOptions.length === 1;
                const manualOverride = testOverrides[binding.variable_name] ?? '';
                const showManualOverride = !isRecordResolved && (!autoSelectedBomSource || String(manualOverride).trim() !== '');
                if (isRecordResolved && String(manualOverride).trim() === '' && binding.source_type !== 'dependent_value') {
                  return null;
                }
                return (
                  <Row key={binding.id || binding.variable_name} gutter={12} align="middle">
                    <Col span={10}>
                      <div className="font-medium">{binding.display_name || binding.variable_name}</div>
                      <div className="text-xs text-gray-500">
                        {binding.variable_name} · {binding.source_type}
                        {binding.source_record_code ? ` · ${binding.source_record_code}` : ''}
                      </div>
                    </Col>
                    <Col span={14}>
                      {needsDiscriminatorFilters ? (
                        // "add. notes with i icon ... this is temp value actual value is at
                        // dispensing" — same note, same reason: nothing typed here is saved,
                        // it only stands in for the real Lot for this one test run.
                        <div className="flex flex-col gap-2">
                          <div className="text-xs text-gray-500 flex items-center gap-1">
                            Needs {mapping.value_discriminator_columns.map(humanizeColumn).join(' + ')} to find a {mapping.value_model_label} record for this test
                            <Tooltip title="Temporary value for this test only — it isn't saved anywhere. The real value this binding uses once wired up is resolved live (e.g. at dispensing), not from here.">
                              <InfoCircleOutlined style={{ color: '#94a3b8' }} />
                            </Tooltip>
                          </div>
                          {mapping.value_discriminator_columns.map((column) => (
                            <div key={column}>
                              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>
                                {humanizeColumn(column)} <span className="font-mono text-gray-400">({column})</span>
                              </div>
                              <Input
                                size="large"
                                placeholder={`Enter ${humanizeColumn(column).toLowerCase()}`}
                                value={(testSourceConfigOverrides[binding.variable_name] || {})[column] ?? ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setTestSourceConfigOverrides((prev) => ({
                                    ...prev,
                                    [binding.variable_name]: { ...(prev[binding.variable_name] || {}), [column]: value },
                                  }));
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      ) : binding.source_type === 'dependent_value' ? (
                        <Input
                          size="large"
                          placeholder="Dependent value for test"
                          value={testDependentValues[dependentKey] ?? ''}
                          onChange={(e) => {
                            setTestDependentValues((prev) => ({ ...prev, [dependentKey]: e.target.value }));
                          }}
                        />
                      ) : needsBomSourcePick ? (
                        <div className="flex flex-col gap-2">
                          {!autoSelectedBomSource && (
                            <Select
                              size="large"
                              allowClear
                              showSearch
                              optionFilterProp="label"
                              placeholder="Pick value from BOM table"
                              value={testBomSources[binding.variable_name] || undefined}
                              options={filteredBomValueOptions}
                              onChange={(val) => {
                                setTestBomSources((prev) => ({ ...prev, [binding.variable_name]: val || null }));
                              }}
                            />
                          )}
                          {showManualOverride && (
                            <Input
                              size="large"
                              placeholder={!autoSelectedBomSource ? 'Or type sample override directly' : 'Type override only if you want to replace the auto-picked value'}
                              value={manualOverride}
                              onChange={(e) => {
                                setTestOverrides((prev) => ({ ...prev, [binding.variable_name]: e.target.value }));
                              }}
                              suffix={<Tooltip title="Temporary value for this test only — it isn't saved anywhere. The real value this binding uses once wired up is resolved live (e.g. at dispensing), not from here.">
                                <InfoCircleOutlined style={{ color: '#94a3b8' }} />
                              </Tooltip>}
                            />
                          )}
                        </div>
                      ) : (
                        <Input
                          size="large"
                          placeholder="Sample override (leave blank to resolve via usage)"
                          value={testOverrides[binding.variable_name] ?? ''}
                          onChange={(e) => {
                            setTestOverrides((prev) => ({ ...prev, [binding.variable_name]: e.target.value }));
                          }}
                          suffix={<Tooltip title="Temporary value for this test only — it isn't saved anywhere. The real value this binding uses once wired up is resolved live (e.g. at dispensing), not from here.">
                            <InfoCircleOutlined style={{ color: '#94a3b8' }} />
                          </Tooltip>}
                        />
                      )}
                    </Col>
                  </Row>
                );
              })}
              <Button type="primary" size="large" loading={testingFormula} onClick={runFormulaTest}>
                Run Test
              </Button>
              {testResult?.ok && (
                <Alert
                  type="success"
                  showIcon
                  message={`Result: ${testResult.data.final_result} ${testResult.data.result_unit_code || ''}`}
                  description={
                    <div style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      raw: {String(testResult.data.raw_result)}
                      <br />
                      {Object.entries(testResult.data.input_snapshot || {}).map(([k, v]) => (
                        <div key={k}>
                          {k} = {String(v.value)} ({v.source_type})
                        </div>
                      ))}
                    </div>
                  }
                />
              )}
              {testResult && !testResult.ok && (
                <Alert type="error" showIcon message="Test failed" description={testResult.error} />
              )}
            </>
          )}
        </div>
      </Modal>

      <Modal
        open={lookupState.open}
        title={lookupState.title || 'Pick a record'}
        footer={null}
        onCancel={closeLookup}
        width={920}
        destroyOnHidden
      >
        {lookupState.modelLabel && (
          <CascadingRecordPicker
            modelLabel={lookupState.modelLabel}
            label={capitalize(modelVerboseNames[lookupState.modelLabel]) || lookupState.modelLabel}
            filters={pickerFiltersForModel[lookupState.modelLabel] || {}}
            extraColumns={extraColumnsForModel[lookupState.modelLabel] || []}
            value={
              lookupState.target === 'batch'
                ? testBatchId
                : lookupState.target?.kind === 'binding'
                  ? testValueRecordIdsByBinding[lookupState.target.variableName] || ''
                : testValueRecordIds[lookupState.target] || ''
            }
            onChange={applyLookupValue}
          />
        )}
      </Modal>
    </div>
  );
}
